/**
 * Device Communication Service
 *
 * Provides hybrid direct/cloud communication for device operations.
 *
 * Key Features:
 * - Direct communication (Electron only) via local/public IP
 * - Cloud fallback (always available) via backend API
 * - 3-step fallback: local IP → public IP → cloud proxy
 * - Web browsers always use cloud (HTTPS security constraint)
 *
 * Architecture:
 * - Web Browser: HTTPS only → Must use cloud API
 * - Electron App: Can make HTTP requests → Can use direct mode
 */

import { api } from './api';
import { isElectron } from '../utils/platform';
import { logger } from './logger';
import { smartSelectDeviceIp } from '../utils/deviceProbe';
import { toCardValue, type RfidTagFormat } from '../utils/rfidTagFormat';
import {
  AdapterFactory,
  PersonData,
  DeviceOperation as AdapterDeviceOperation,
  DeviceInfo as AdapterDeviceInfo,
  sessionManager,
  SessionInfo
} from './adapters';
import { fetchImageAsBase64, getFullImageUrl, resizeBase64Image } from '../utils/imageUtils';
import { deviceBaseUrl } from '../utils/deviceEndpoint';

// Stored profile/identity photos are 800×800 — far larger than any facial-recognition
// device needs for accurate matching. Downsizing before enrollment shrinks the payload
// sent over Electron's local device connections and the cloud relay alike.
const DEVICE_FACE_PHOTO_MAX_DIMENSION = 640;

/**
 * Communication mode for device operations
 */
export enum CommunicationMode {
  DIRECT = 'direct',      // Electron → Device (local/public IP)
  CLOUD = 'cloud',        // Web/Electron → Backend → Device
  HYBRID = 'hybrid'       // Try direct first, fallback to cloud
}

/**
 * Source of the successful operation
 */
export type OperationSource = 'local' | 'public' | 'cloud';

/**
 * Result of a device operation
 */
export interface DeviceOperationResult {
  success: boolean;
  data?: any;
  error?: string;
  mode: CommunicationMode;
  latencyMs: number;
  source: OperationSource;
  attempts: {
    address: string;
    result: 'success' | 'timeout' | 'error';
    error?: string;
    latencyMs?: number;
  }[];
  /**
   * True only when a SEPARATE read-back request (not just "the delete/write call
   * didn't throw") independently confirmed the device's actual state — e.g. a
   * load_objects.fcgi re-query after destroy_objects.fcgi found no matching user.
   * Undefined/false means only the write command's own response was trusted, which
   * is a weaker signal — see removeControlIDPerson for the one place this is
   * currently computed.
   */
  readbackVerified?: boolean;
  /**
   * SYNC_PERSON with a photo: whether the face image write to the device
   * completed. For a facial reader the face IS the credential — a sync whose
   * face upload failed is reported as success:false, never silently swallowed.
   */
  faceUploaded?: boolean;
  /**
   * True only when a separate read-back (Hikvision: targeted UserInfo/Search
   * returning numOfFace > 0) confirmed the device actually holds a face for
   * this person after the upload.
   */
  faceVerified?: boolean;
}

/**
 * Compute the ControlID sec_box action ID for a given relay (saída).
 *
 * 65793 = 0x010101 → portaria=1, type=1, relay=1
 * Structure: (portaria << 16) | (portaria << 8) | relay = portaria * 65792 + relay
 *
 * For iDUHF (single portaria=1):
 *   saída 1 (Relé Primário)   → 65793  (portaria 1, relay 1)
 *   saída 2 (Relé Secundário) → 65794  (portaria 1, relay 2)
 *
 * For multi-portaria devices pass portaria explicitly via controlid_sec_box_id override.
 */
export function controlIDSecBoxId(saida: number, portaria: number = 1): number {
  return portaria * 65792 + saida;
}

/**
 * Device information with credentials
 */
/**
 * Hikvision's ways of saying "a face is already stored for this FPID". The upload is
 * refused until the stored one is cleared, so these errors mean "replace it", not "give up".
 */
const FACE_ALREADY_EXISTS_PATTERN =
  /deviceUserAlreadyExistFace|FaceExistAlready|FaceIDAlreadyExist/i;

export interface DeviceInfo {
  id: string;
  /** Human-readable device name — what the operator sees in toasts/logs. Falls back to `id`. */
  name?: string;
  ip_address?: string;        // Cloud/public IP (e.g., "hikddns.com:1020")
  local_ip_address?: string;  // Local/private IP (e.g., "192.168.1.100")
  username: string;
  password: string;
  vendor: 'hikvision' | 'controlid' | 'rfid';
  port?: string;
  /** Zone entry type — 'entrada' = entry only, 'saida' = exit only, 'passagem' = both */
  direction?: 'entrada' | 'saida' | 'passagem';
  /** Device type for sub-vendor routing (e.g. 'controlid_rfid' vs 'controlid_face') */
  device_type?: string;
  /** Device config — carries vendor-specific settings (e.g. controlid_sec_box_id, controlid_door_number) */
  config?: Record<string, any>;
}

/**
 * Maps a raw API device object to a DeviceInfo for DeviceCommunicationService.
 *
 * Single source of truth for manufacturer/device_type → vendor mapping.
 * Import this instead of writing the mapping inline.
 *
 * For new code, prefer `DeviceIntegration.toHandle()` from `integrations/devices`.
 */
export function apiDeviceToDeviceInfo(
  d: any,
  direction?: 'entrada' | 'saida' | 'passagem',
): DeviceInfo {
  const mfr = d.manufacturer?.toLowerCase() ?? '';
  const vendorByType: Record<string, DeviceInfo['vendor']> = {
    facial_recognition: 'hikvision',
    rfid_reader:        'rfid',
    access_controller:  'controlid',
    controlid_face:     'controlid',
    controlid_rfid:     'controlid',
  };
  // Substring match, not exact equality — ControlID's iDBox/iDFace lines report
  // their manufacturer as "idbox"/"idface", not literally "controlid". Matching
  // only "controlid" here silently misclassified those devices as 'hikvision'
  // (the vendorByType fallback), which broke direct communication entirely.
  const isControlIDMfr = mfr.includes('controlid') || mfr.includes('idface') || mfr.includes('idbox');
  const vendor: DeviceInfo['vendor'] =
    isControlIDMfr ? 'controlid' :
    mfr === 'hikvision' ? 'hikvision' :
    vendorByType[d.device_type] ?? 'hikvision';

  return {
    id:               d.id,
    name:             d.name || undefined,
    vendor,
    device_type:      d.device_type,
    ip_address:       d.ip_address,
    local_ip_address: d.local_ip_address,
    port:             d.port,
    username:         d.username || 'admin',
    password:         d.password || '',
    config:           d.config,
    direction,
  };
}

/**
 * Device operation definition
 */
export interface DeviceOperation {
  type: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  hikEndpoint?: string;      // Hikvision ISAPI endpoint
  ctrlEndpoint?: string;     // ControlID endpoint
  rfidEndpoint?: string;     // RFID endpoint
  cloudEndpoint: string;     // Backend API endpoint
}

/**
 * Standard device operations
 */
export const DeviceOperations = {
  SYNC_PERSON: {
    type: 'sync_person',
    method: 'POST',
    hikEndpoint: 'AccessControl/UserInfo/Record',  // Changed from SetUp to Record
    ctrlEndpoint: 'create_objects.fcgi',  // NEW ControlID database-style API
    rfidEndpoint: 'users/add',
    cloudEndpoint: 'sync-person'
  } as DeviceOperation,

  REMOVE_PERSON: {
    type: 'remove_person',
    method: 'PUT',
    hikEndpoint: 'AccessControl/UserInfo/Delete',
    ctrlEndpoint: 'remove_user.fcgi',
    rfidEndpoint: 'users/remove',
    cloudEndpoint: 'remove-person'
  } as DeviceOperation,

  CHECK_HEALTH: {
    type: 'check_health',
    method: 'GET',
    hikEndpoint: 'System/deviceInfo',
    ctrlEndpoint: 'device_information.fcgi',
    rfidEndpoint: 'status',
    cloudEndpoint: 'health'
  } as DeviceOperation,

  UPDATE_PERSON: {
    type: 'update_person',
    method: 'PUT',
    hikEndpoint: 'AccessControl/UserInfo/Modify',
    ctrlEndpoint: 'update_user.fcgi',
    rfidEndpoint: 'users/update',
    cloudEndpoint: 'update-person'
  } as DeviceOperation,

  LOAD_PEOPLE: {
    type: 'load_people',
    method: 'POST',
    hikEndpoint: 'AccessControl/UserInfo/Search',
    ctrlEndpoint: 'load_objects.fcgi',
    rfidEndpoint: 'users/list',
    cloudEndpoint: 'people'
  } as DeviceOperation,

  OPEN_DOOR: {
    type: 'open_door',
    method: 'PUT',
    cloudEndpoint: 'door/open',
  } as DeviceOperation,

  // Configures the device to push events to the cloud webhook endpoint.
  // Electron mode: calls Hikvision device directly (PUT XML to ISAPI).
  // ControlID / web: always falls back to cloud.
  CONFIGURE_WEBHOOK: {
    type: 'configure_webhook',
    method: 'PUT',
    cloudEndpoint: 'configure-webhook',
  } as DeviceOperation,

  ENROLL_FINGERPRINT: {
    type: 'enroll_fingerprint',
    method: 'POST',
    ctrlEndpoint: 'enroll_finger.fcgi',
    cloudEndpoint: 'enroll-fingerprint',
  } as DeviceOperation,

  FETCH_FINGERPRINTS: {
    type: 'fetch_fingerprints',
    method: 'POST',
    ctrlEndpoint: 'load_objects.fcgi',
    cloudEndpoint: 'fetch-fingerprints',
  } as DeviceOperation,

  PUSH_FINGERPRINT: {
    type: 'push_fingerprint',
    method: 'POST',
    ctrlEndpoint: 'create_objects.fcgi',
    cloudEndpoint: 'push-fingerprint',
  } as DeviceOperation,

  DELETE_FINGERPRINT: {
    type: 'delete_fingerprint',
    method: 'POST',
    ctrlEndpoint: 'destroy_objects.fcgi',
    cloudEndpoint: 'delete-fingerprint',
  } as DeviceOperation,
};

/**
 * Main device communication orchestrator
 * Intelligently routes operations via direct or cloud paths
 */
export class DeviceCommunicationService {
  private metricsEnabled: boolean = true;
  // Cache for ControlID default group ID per device (avoids re-querying every sync loop)
  private controlIDGroupCache = new Map<string, number>();

  /** Clear the cached group ID for a device, forcing a full re-check on next call */
  clearControlIDGroupCache(deviceId: string): void {
    this.controlIDGroupCache.delete(deviceId);
    console.log(` [ControlID] Cleared group cache for device ${deviceId}`);
  }

  /**
   * Execute device operation with intelligent routing
   *
   * @param device - Device info with credentials
   * @param operation - Operation type (sync, add, remove, etc.)
   * @param payload - Operation-specific data
   * @param mode - Force specific mode or use HYBRID (default)
   */
  async executeOperation(
    device: DeviceInfo,
    operation: DeviceOperation,
    payload: any,
    mode: CommunicationMode = CommunicationMode.HYBRID
  ): Promise<DeviceOperationResult> {
    const startTime = Date.now();
    const attempts: DeviceOperationResult['attempts'] = [];

    logger.debug('deviceComm', 'Executing operation', {
      deviceId: device.id,
      vendor: device.vendor,
      operationType: operation.type,
      mode,
      hasLocalIp: !!device.local_ip_address,
      hasPublicIp: !!device.ip_address,
      isElectron: isElectron(),
    });

    // Strategy selection
    if (mode === CommunicationMode.CLOUD || this.mustUseCloud()) {
      return this.cloudOperation(device, operation, payload, startTime, attempts);
    }

    if (mode === CommunicationMode.DIRECT && this.canUseDirect(device)) {
      return this.directOperation(device, operation, payload, startTime, attempts);
    }

    // HYBRID: Smart routing (Electron only) via shared deviceProbe utility.
    //
    // usePublicIpAsDirect ON (user preference): skip probe, always try public IP directly.
    // Default (smart mode): smartSelectDeviceIp probes both addresses in parallel; if both
    //   fail it re-probes once before accepting the verdict, then goes straight to cloud.
    if (this.canUseDirect(device)) {
      const usePublicIpAsDirect = (() => {
        try {
          const saved = localStorage.getItem('operational_view_settings');
          return saved ? (JSON.parse(saved)?.usePublicIpAsDirect ?? false) : false;
        } catch { return false; }
      })();

      if (usePublicIpAsDirect) {
        // User explicitly prefers public IP — bypass probe
        const directIp = device.ip_address;
        if (!directIp) {
          console.warn(`[Device Sync] Device ${device.id} has no public ip_address configured`);
        } else {
          try {
            console.log('[Device Sync] usePublicIpAsDirect → public IP:', directIp, '(12s)');
            return await this.directOperationWithTimeout(
              { ...device, ip_address: directIp }, operation, payload, startTime, 12000, 'public', attempts
            );
          } catch (err) {
            console.log(' [Device Sync] Public IP failed, falling to cloud', { error: String(err) });
            attempts.push({ address: directIp, result: 'error', error: String(err) });
          }
        }
      } else {
        // Smart routing: probe → pick best reachable address → direct → cloud
        // Budgets match checkDeviceReachability (400/1500). They used to be tighter here
        // (300/900), so the same device could be "online" in the health badge and
        // "unreachable" to an operation started one second later.
        //
        // retryOnUnreachable: an operation is worth a second probe. Embedded HTTP servers RST a
        // SYN once their small connection backlog is full, so one refusal does not mean offline
        // — hence a sync failing with "dispositivo inacessível" and then working on a retry.
        const selected = await smartSelectDeviceIp(device, {
          timeoutLocal: 400,
          timeoutPublic: 1500,
          retryOnUnreachable: true,
        });
        if (selected && selected.source === 'unreachable') {
          // Probe actively confirmed neither address responds — don't waste a
          // 10-12s HTTP timeout retrying an address already proven dead, skip
          // straight to cloud.
          console.log(`[Device Sync] Probe confirmed device unreachable on both addresses, skipping direct attempt`);
          attempts.push({ address: selected.ip, result: 'error', error: 'Probed unreachable (TCP)' });
        } else if (selected) {
          const opSource: OperationSource = selected.source === 'local' ? 'local' : 'public';
          const timeout = selected.source === 'local' ? 12000 : 10000;
          console.log(`[Device Sync] Probe → ${selected.source} (${selected.ip}), attempting direct (${timeout / 1000}s)`);
          try {
            return await this.directOperationWithTimeout(
              { ...device, ip_address: selected.ip }, operation, payload, startTime, timeout, opSource, attempts
            );
          } catch (err) {
            console.log(` [Device Sync] Direct via ${selected.source} failed, falling to cloud`, { error: String(err) });
            attempts.push({ address: selected.ip, result: 'error', error: String(err) });
          }
        } else {
          // Probe timed out (e.g. 400ms too tight for a WAN IP stored in local_ip_address).
          // Try direct anyway with the stored IP before giving up to cloud.
          const fallbackIp = device.local_ip_address || device.ip_address;
          if (fallbackIp) {
            console.log('[Device Sync] Probe timeout, trying direct with fallback IP:', fallbackIp);
            try {
              return await this.directOperationWithTimeout(
                { ...device, ip_address: fallbackIp }, operation, payload, startTime, 10000, 'local', attempts
              );
            } catch (err) {
              console.log('[Device Sync] Direct fallback also failed, trying cloud', { error: String(err) });
              attempts.push({ address: fallbackIp, result: 'error', error: String(err) });
            }
          } else {
            console.log('[Device Sync] No reachable IP via probe, falling to cloud');
          }
        }
      }
    }

    // Step 3: Fallback to cloud proxy.
    // EXCEPTION: sync_person and remove_person have no working cloud endpoint.
    // - sync_person: callers use /customers/:id/people/:id/sync directly
    // - remove_person: backend's triggerIoTRemoval goroutine handles cloud removal when
    //   local_removal_done=false is sent to /deferred-removals/:id/trigger. The frontend
    //   must never call iot-connect DELETE directly — it causes 504 cascades when devices are offline.
    if (operation.type === 'sync_person' || operation.type === 'remove_person') {
      console.log(` [Device Sync] Skipping cloud step for ${operation.type} — backend handles cloud path`);
      return {
        success: false,
        error: 'Comunicação direta falhou — dispositivo inacessível',
        mode: CommunicationMode.HYBRID,
        latencyMs: Date.now() - startTime,
        source: 'cloud' as OperationSource,
        attempts,
      };
    }
    console.log(' [Device Sync] Both direct attempts failed, using cloud proxy (backend API)...');
    logger.info('deviceComm', 'Using cloud proxy fallback');
    return this.cloudOperation(device, operation, payload, startTime, attempts);
  }

  /**
   * Check if we can use direct communication
   */
  private canUseDirect(device: DeviceInfo): boolean {
    // Must be Electron app
    if (!isElectron()) {
      return false;
    }

    // Must have at least one IP address (local or public)
    if (!device.local_ip_address && !device.ip_address) {
      return false;
    }

    // Must have Electron API available
    if (typeof window === 'undefined' || !window.electronAPI?.deviceHttpRequest) {
      logger.warn('deviceComm', 'Electron API not available');
      return false;
    }

    return true;
  }

  /**
   * Check if we must use cloud (browser restriction)
   */
  private mustUseCloud(): boolean {
    // Web browsers must use cloud (HTTPS → HTTP blocked)
    return !isElectron();
  }

  /**
   * Direct device communication with timeout (Electron only)
   * Implements JavaScript-level timeout to ensure requests don't hang indefinitely
   */
  private async directOperationWithTimeout(
    device: DeviceInfo,
    operation: DeviceOperation,
    payload: any,
    startTime: number,
    timeout: number,
    source: OperationSource,
    attempts: DeviceOperationResult['attempts']
  ): Promise<DeviceOperationResult> {
    const attemptStart = Date.now();

    try {
      // Create a timeout promise that rejects after the specified timeout
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`ETIMEDOUT: Request timed out after ${timeout}ms`));
        }, timeout);
      });

      // Race the actual operation against the timeout
      // Whichever completes first wins
      const result = await Promise.race([
        this.directOperation(
          device,
          operation,
          payload,
          startTime,
          attempts,
          timeout,
          source
        ),
        timeoutPromise
      ]);

      attempts.push({
        address: device.ip_address || device.local_ip_address || 'unknown',
        result: 'success',
        latencyMs: Date.now() - attemptStart,
      });

      return result;
    } catch (error) {
      const latencyMs = Date.now() - attemptStart;

      attempts.push({
        address: device.ip_address || device.local_ip_address || 'unknown',
        result: latencyMs >= timeout ? 'timeout' : 'error',
        error: String(error),
        latencyMs,
      });

      throw error;
    }
  }

  /**
   * Direct device communication (Electron only)
   */
  private async directOperation(
    device: DeviceInfo,
    operation: DeviceOperation,
    payload: any,
    startTime: number,
    attempts: DeviceOperationResult['attempts'],
    timeout: number = 15000,  // Increased from 3000ms to 15000ms (15 seconds)
    source: OperationSource = 'local',
    retryCount: number = 0  // Track retry attempts to prevent infinite loops
  ): Promise<DeviceOperationResult> {
    // Special paths: configure cloud address — call device directly without the adapter pipeline
    if (operation.type === 'configure_webhook' && device.vendor === 'hikvision') {
      return this.directHikvisionConfigureWebhook(device, payload, startTime, attempts, source, timeout);
    }
    if (operation.type === 'configure_webhook' && device.vendor === 'controlid') {
      return this.directControlIDConfigureMonitor(device, payload, startTime, attempts, source, timeout);
    }
    if (operation.type === 'open_door' && device.vendor === 'controlid') {
      return this.openControlIDDoor(device, payload, startTime, attempts, source, timeout);
    }

    // Prevent infinite retry loops - max 2 retries:
    //   retryCount=0: first attempt (session auth if required)
    //   retryCount=1: retry with new session (session expired)
    //   retryCount=2: digest-only fallback (device rejects session auth for this endpoint)
    if (retryCount > 2) {
      console.error(` [DeviceComm] Max retries (${retryCount}) exceeded for device ${device.id}`);
      return {
        success: false,
        attempts,
        error: 'Max retry attempts exceeded',
        mode: CommunicationMode.DIRECT,
        latencyMs: Date.now() - startTime,
        source,
      };
    }

    // Check if we should use adapter-based formatting
    const useAdapter = this.shouldUseAdapter(operation);

    let endpoint: string;
    let method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    let formattedPayload: any;

    if (useAdapter && AdapterFactory.hasAdapter(device.vendor)) {
      // Use vendor-specific adapter
      console.log(' [Using Adapter] Vendor:', device.vendor);

      const adapter = AdapterFactory.getAdapter(device.vendor);
      const adapterOperation = this.mapToAdapterOperation(operation);

      // Format payload using adapter
      if (adapterOperation === AdapterDeviceOperation.SYNC_PERSON) {
        // ControlID: use check-first upsert path (avoids duplicates)
        if (device.vendor === 'controlid') {
          return await this.syncControlIDPerson(device, payload, startTime, attempts, source, timeout);
        }
        formattedPayload = await adapter.formatSyncPersonPayload(payload);
      } else if (adapterOperation === AdapterDeviceOperation.REMOVE_PERSON) {
        // ControlID: use load-then-delete to get the actual numeric ID on the device
        if (device.vendor === 'controlid') {
          return await this.removeControlIDPerson(device, payload, startTime, attempts, source, timeout);
        }
        // Use ?? (nullish coalescing) NOT || so empty string "" is preserved and not skipped
        const personIdToRemove = payload.devicePersonId ?? payload.person_id;
        if (!personIdToRemove && personIdToRemove !== 0) {
          throw new Error('REMOVE_PERSON: devicePersonId is required and cannot be empty');
        }
        // Derive the vendor-specific device ID (e.g. md5 for Hikvision) the same way SYNC_PERSON does.
        // If devicePersonId was already provided explicitly, trust it as-is.
        const devicePersonId = (payload.devicePersonId != null)
          ? String(payload.devicePersonId)
          : ((adapter as any).generateDevicePersonId?.({ person_id: String(personIdToRemove) }) ?? String(personIdToRemove));
        formattedPayload = adapter.formatRemovePersonPayload(devicePersonId);
      } else if (adapterOperation === AdapterDeviceOperation.LOAD_PEOPLE) {
        formattedPayload = adapter.formatLoadPeoplePayload(payload);
      } else if (adapterOperation === AdapterDeviceOperation.OPEN_DOOR) {
        formattedPayload = adapter.formatOpenDoorPayload?.() ?? {};
      } else {
        formattedPayload = payload;
      }

      // Get endpoint and method from adapter
      endpoint = adapter.getEndpoint(adapterOperation, device);
      method = adapter.getMethod(adapterOperation);

      console.log(' [Vendor Payload] Formatted by adapter:', {
        vendor: device.vendor,
        operation: adapterOperation,
        endpoint,
        method,
        payload: JSON.stringify(formattedPayload, null, 2),
      });
    } else {
      // Use legacy endpoint building
      endpoint = this.buildDeviceEndpoint(device, operation);
      method = operation.method;
      formattedPayload = payload;
    }

    console.log(' [Direct Request] Details:', {
      endpoint,
      method,
      timeout,
      source,
      deviceIp: device.ip_address || device.local_ip_address,
      vendor: device.vendor,
      hasUsername: !!device.username,
      hasPassword: !!device.password,
      usedAdapter: useAdapter,
    });

    console.log(' [Direct Request] Payload:', JSON.stringify(formattedPayload, null, 2));

    logger.debug('deviceComm', 'Making direct request', {
      endpoint,
      method,
      timeout,
      source,
    });

    // ==================== SESSION AUTHENTICATION ====================
    // Check if device requires session-based authentication
    let sessionInfo: SessionInfo | null = null;
    let requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json; charset=utf-8',
      'Accept': 'application/json',
    };

    // Door open uses digest auth on all Hikvision firmware versions — skip firmware
    // detection to avoid wasting the 2-second local-IP timeout on an extra HTTP roundtrip.
    // REMOVE_PERSON on Hikvision also skips session auth: the session handshake is incomplete
    // (missing Step 2 of the challenge-response) which causes 401 on every attempt. Digest auth
    // (sent via digestAuth + authFallback:'digest') works reliably on all Hikvision firmware.
    // retryCount >= 2: two consecutive 401s with session auth → device doesn't support session
    // auth for this endpoint (e.g. some Hikvision models reject session auth for AccessControl
    // ISAPI but accept digest). Fall back to digest-only for this final attempt.
    const skipSessionAuth = (device.vendor === 'hikvision' && operation.type === 'remove_person') || retryCount >= 2;
    if (!skipSessionAuth && useAdapter && AdapterFactory.hasAdapter(device.vendor) && operation.type !== 'open_door') {
      const adapter = AdapterFactory.getAdapter(device.vendor);

      // Check if session auth is required
      const requiresSession = await adapter.requiresSessionAuth(device);

      if (requiresSession) {
        console.log(` [DeviceComm] Device ${device.id} requires session authentication`);

        // Get or create session
        sessionInfo = await sessionManager.getOrCreateSession(device, adapter);

        if (sessionInfo) {
          // Format URL with session parameters
          endpoint = adapter.formatUrlWithSession(endpoint, sessionInfo);

          // Format headers with session information
          requestHeaders = adapter.formatHeadersWithSession(requestHeaders, sessionInfo);

          console.log(` [DeviceComm] Using session auth for ${device.vendor} device ${device.id}`);
        } else {
          console.warn(` [DeviceComm] Failed to establish session for device ${device.id}, will try digest auth`);
        }
      }
    }

    // For XML payloads (e.g. Hikvision OPEN_DOOR), override Content-Type and use raw string body
    let requestBody: any = formattedPayload;
    if (formattedPayload?._xml) {
      requestHeaders['Content-Type'] = 'application/xml';
      requestBody = formattedPayload.content;
    }

    const result = await window.electronAPI!.deviceHttpRequest({
      url: endpoint,
      method: method,
      headers: requestHeaders,
      body: requestBody,
      timeout,
      sessionAuth: sessionInfo ? {
        token: sessionInfo.token,
        headers: sessionInfo.headers,
        queryParams: sessionInfo.queryParams
      } : undefined,
      digestAuth: {
        username: device.username,
        password: device.password
      },
      authFallback: 'digest'
    } as any) as any;

    console.log(' [Direct Request] Result:', {
      success: result.success,
      hasData: !!result.data,
      error: result.error,
      data: result.data,
      statusCode: result.statusCode,
    });

    // Handle 401 (session expired) - invalidate and retry once
    // BUT: Don't retry if account is locked
    if (!result.success && result.statusCode === 401 && sessionInfo && retryCount === 0) {
      // Check if account is locked
      if (result.error?.includes('<lockStatus>lock</lockStatus>')) {
        console.error(' [DeviceComm] Account is locked, NOT retrying');
        throw new Error('Device account is locked due to too many failed login attempts. Please wait for unlock.');
      }

      console.log(' [DeviceComm] Session expired (HTTP 401), invalidating and retrying (attempt 1)...');
      sessionManager.invalidateSession(device);

      // Retry once with new session
      return this.directOperation(device, operation, payload, startTime, attempts, timeout, source, retryCount + 1);
    }

    // Handle persistent 401 with session auth (retryCount=1 = second failure).
    // Some Hikvision devices accept session auth for System endpoints but reject it
    // for AccessControl endpoints (responds 401 with isActivated:false). Since digest
    // auth works on the same device, fall back to digest-only for the final attempt.
    // Also update the firmware cache so all future requests (including uploadFacePhoto)
    // skip session establishment entirely for this device.
    if (!result.success && result.statusCode === 401 && sessionInfo && retryCount === 1) {
      console.log(' [DeviceComm] Session auth rejected again (HTTP 401) — falling back to digest-only...');
      sessionManager.invalidateSession(device);
      if (device.vendor === 'hikvision' && AdapterFactory.hasAdapter('hikvision')) {
        (AdapterFactory.getAdapter('hikvision') as any).markAsDigestOnly?.(device);
      }
      return this.directOperation(device, operation, payload, startTime, attempts, timeout, source, retryCount + 1);
    }

    if (!result.success) {
      // Handle "user already exists" error by trying UPDATE instead.
      // Hikvision devices may return employeeNoAlreadyExist OR checkUser/writeDatabaseError
      // (error code 0x30000011) when the user already exists in the device database.
      const isUserAlreadyExistsError = result.error?.includes('employeeNoAlreadyExist') ||
        (result.error?.includes('checkUser') && result.error?.includes('writeDatabaseError'));
      if (isUserAlreadyExistsError && useAdapter && operation.type === 'sync_person') {
        console.log(' [Direct Request] User already exists, retrying with UPDATE endpoint');

        // Get UPDATE endpoint from adapter
        const updateEndpoint = endpoint.replace('/UserInfo/Record', '/UserInfo/Modify');
        const updateMethod = 'PUT';

        console.log(' [Direct Request] Retrying with UPDATE:', {
          newEndpoint: updateEndpoint,
          method: updateMethod,
        });

        const updateResult = await window.electronAPI!.deviceHttpRequest({
          url: updateEndpoint,
          method: updateMethod,
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'Accept': 'application/json',
          },
          body: formattedPayload,
          timeout,
          digestAuth: {
            username: device.username,
            password: device.password
          }
        } as any);

        if (updateResult.success) {
          console.log(' [Direct Request] UPDATE succeeded after CREATE failed');
          const updateOpResult: DeviceOperationResult = {
            success: true,
            data: updateResult.data,
            error: undefined,
            mode: CommunicationMode.DIRECT,
            latencyMs: Date.now() - startTime,
            source: source,
            attempts,
          };
          // Face photo for the EXISTING user (the "person already on device,
          // photo added later" case): awaited and read-back-verified — a
          // swallowed failure here used to report success with no usable face.
          const updatePhotoProvided = !!(payload.identity_photo_url || payload.profile_picture_url);
          if (updatePhotoProvided && useAdapter && AdapterFactory.hasAdapter(device.vendor)) {
            const adapter = AdapterFactory.getAdapter(device.vendor);
            const deviceUserId = adapter.generateDevicePersonId(payload);
            const face = await this.uploadAndVerifyFace(device, deviceUserId, payload, timeout);
            updateOpResult.faceUploaded = face.faceUploaded;
            updateOpResult.faceVerified = face.faceVerified;
            if (face.error) {
              console.error(' [Direct Request] Face not confirmed after UPDATE:', face.error);
              return {
                ...updateOpResult,
                success: false,
                error: `user updated but ${face.error}`,
              };
            }
          }
          return updateOpResult;
        } else {
          console.error(' [Direct Request] UPDATE also failed:', updateResult.error);
        }
      }

      throw new Error(result.error || 'Direct operation failed');
    }

    const operationResult: DeviceOperationResult = {
      success: result.success,
      data: result.data,
      error: result.error,
      mode: CommunicationMode.DIRECT,
      latencyMs: Date.now() - startTime,
      source: source,
      attempts,
    };

    // If this was a successful SYNC_PERSON operation, upload face photo
    if (useAdapter && operation.type === 'sync_person' && AdapterFactory.hasAdapter(device.vendor)) {
      console.log(' [DirectOperation] Sync succeeded, attempting face upload');
      console.log(' [DirectOperation] Person data for face upload:', {
        person_id: payload.person_id,
        has_identity_photo: !!payload.identity_photo_url,
        has_profile_picture: !!payload.profile_picture_url,
        identity_photo_url: payload.identity_photo_url,
        profile_picture_url: payload.profile_picture_url,
      });
      console.log(' [DirectOperation] Device response data:', operationResult.data);

      // Get the adapter
      const adapter = AdapterFactory.getAdapter(device.vendor);

      // Extract numeric device user ID from response
      // ControlID create_objects returns: { "ids": [123] }
      let deviceUserId: string | number;

      if (device.vendor === 'controlid' && operationResult.data?.ids && operationResult.data.ids.length > 0) {
        // Use numeric ID from device response
        deviceUserId = operationResult.data.ids[0];
        console.log(' [DirectOperation] Using numeric device user ID from response:', deviceUserId);
      } else {
        // Fallback to MD5 hash for other vendors (Hikvision, etc.)
        deviceUserId = adapter.generateDevicePersonId(payload);
        console.log(' [DirectOperation] Using generated employeeNo:', deviceUserId);
      }

      // Save enrollment record so event authorization works (non-blocking) —
      // the USER record is on the device regardless of what happens to the face.
      // When syncing directly (Electron), iot-connect's AddPersonToDevice is not called,
      // so person_device_enrollments is never written. We fix that here for both Hikvision
      // and ControlID. The device_employee_no stored is the actual numeric ID returned by the
      // device (deviceUserId) so the backend auth checker can resolve events via Tier 1 lookup.
      if ((device.vendor === 'hikvision' || device.vendor === 'controlid') && payload.person_id && device.id) {
        const employeeNoForEnrollment = String(deviceUserId);
        api.post(
          `/iot-connect/facial-recognition/devices/${device.id}/people/${payload.person_id}/enrollment`,
          { device_employee_no: employeeNoForEnrollment }
        ).then(() => {
          console.log(' [DirectOperation] Enrollment record saved', { vendor: device.vendor, employeeNo: employeeNoForEnrollment });
        }).catch(err => {
          console.warn(' [DirectOperation] Failed to save enrollment record:', err?.message);
        });
      }

      // Face photo: when the payload carries one, the upload is AWAITED and
      // read-back-verified. A facial credential without a face is not synced —
      // a swallowed upload failure here used to report "Sucesso" while the
      // person could not open the door.
      const photoProvided = !!(payload.identity_photo_url || payload.profile_picture_url);
      if (photoProvided) {
        const face = await this.uploadAndVerifyFace(device, deviceUserId, payload, timeout);
        operationResult.faceUploaded = face.faceUploaded;
        operationResult.faceVerified = face.faceVerified;
        if (face.error) {
          console.error(' [DirectOperation] Face not confirmed on device:', face.error);
          return {
            ...operationResult,
            success: false,
            error: `user synced but ${face.error}`,
          };
        }
        console.log(' [DirectOperation] Face upload completed', { verified: face.faceVerified });
      }
    }

    return operationResult;
  }

  /**
   * Reads back whether the device ACTUALLY holds a face for this person.
   * Hikvision only: the targeted UserInfo/Search (EmployeeNoList = md5(person_id))
   * returns numOfFace for the matched user. Returns true/false on a conclusive
   * device answer, null when unavailable (request failed / other vendor).
   */
  private async hikvisionFacePresent(device: DeviceInfo, personId: string): Promise<boolean | null> {
    if (device.vendor !== 'hikvision' || !personId) return null;
    try {
      const res = await this.executeOperation(
        device, DeviceOperations.LOAD_PEOPLE, { person_id: personId }, CommunicationMode.DIRECT
      );
      if (!res.success) return null;
      const users = res.data?.UserInfoSearch?.UserInfo;
      if (!Array.isArray(users) || users.length === 0) return null;
      const numOfFace = Number(users[0]?.numOfFace ?? 0);
      return numOfFace > 0;
    } catch {
      return null;
    }
  }

  /**
   * Hikvision face writes onto an EXISTING user are unreliable: FaceDataRecord
   * can "succeed" while the device stores nothing (its own comment below:
   * "FaceDataRecord POST fails silently..."). The field-proven workaround is
   * revoke + sync again — the CREATE path lands the face every time. This
   * automates that exact recipe: delete the user record, recreate it from the
   * same payload, and upload the face onto the fresh user.
   */
  private async recreateHikvisionUserWithFace(
    device: DeviceInfo,
    deviceUserId: string | number,
    payload: any,
    timeout: number,
  ): Promise<void> {
    const adapter = AdapterFactory.getAdapter(device.vendor);

    console.warn(' [Face Sync] Face missing after upload — recreating user (automated revoke+sync workaround)');

    const deleteEndpoint = adapter.getEndpoint(AdapterDeviceOperation.REMOVE_PERSON, device);
    await window.electronAPI!.deviceHttpRequest({
      url: deleteEndpoint,
      method: adapter.getMethod(AdapterDeviceOperation.REMOVE_PERSON),
      headers: { 'Content-Type': 'application/json; charset=utf-8', 'Accept': 'application/json' },
      body: adapter.formatRemovePersonPayload(String(deviceUserId)),
      timeout,
      digestAuth: { username: device.username, password: device.password },
      authFallback: 'digest',
    } as any);

    const createEndpoint = adapter.getEndpoint(AdapterDeviceOperation.SYNC_PERSON, device);
    const createResult = await window.electronAPI!.deviceHttpRequest({
      url: createEndpoint,
      method: adapter.getMethod(AdapterDeviceOperation.SYNC_PERSON),
      headers: { 'Content-Type': 'application/json; charset=utf-8', 'Accept': 'application/json' },
      body: await adapter.formatSyncPersonPayload(payload),
      timeout,
      digestAuth: { username: device.username, password: device.password },
      authFallback: 'digest',
    } as any) as any;
    if (!createResult.success) {
      throw new Error(`user recreate failed: ${createResult.error}`);
    }

    await this.uploadFacePhoto(device, deviceUserId, payload, timeout);
  }

  /**
   * Awaits the face upload and read-back-verifies it. Returns null error on
   * full success, or an error message when the face could not be confirmed on
   * the device — the caller converts that into a FAILED sync result, because a
   * facial credential without a face is not a synced credential.
   *
   * When the read-back PROVES the face didn't land on a Hikvision device (the
   * "person already existed on the device" case), the automated revoke+sync
   * workaround (recreateHikvisionUserWithFace) runs once before giving up.
   */
  private async uploadAndVerifyFace(
    device: DeviceInfo,
    deviceUserId: string | number,
    payload: any,
    timeout: number,
  ): Promise<{ faceUploaded: boolean; faceVerified: boolean; error: string | null }> {
    try {
      await this.uploadFacePhoto(device, deviceUserId, payload, timeout);
    } catch (err: any) {
      const message = String(err?.message || err);

      // "A face already exists for this FPID" means the stored photo was never cleared —
      // the pre-upload delete did not take. Giving up here leaves the OLD photo on the
      // device forever, which is the one thing a re-sync is supposed to fix. Deleting and
      // recreating the user is the field-proven way to force the new photo on.
      if (device.vendor === 'hikvision' && FACE_ALREADY_EXISTS_PATTERN.test(message)) {
        // The recovery below DELETES and rebuilds the device user, and Hikvision's
        // formatSyncPersonPayload writes UserInfo only — no cards. A payload that never
        // mentions rfid_tags comes from a caller that did not account for credentials, so
        // recreating could drop a card the device already held. Report the photo failure
        // instead: a stale photo is recoverable, a silently deleted card is not.
        if (!Array.isArray(payload?.rfid_tags)) {
          return {
            faceUploaded: false,
            faceVerified: false,
            error: `face upload failed: ${message}; a substituição exige sincronizar de uma tela que envie as credenciais completas (cartões/tags) — recadastro cancelado para não apagar credenciais do dispositivo`,
          };
        }

        console.warn(' [Face Sync] Device still holds a face for this person — recreating user to replace it');
        try {
          await this.recreateHikvisionUserWithFace(device, deviceUserId, payload, timeout);
        } catch (recreateErr: any) {
          return {
            faceUploaded: false,
            faceVerified: false,
            error: `face upload failed: ${message}; replacing it via user recreate also failed: ${recreateErr?.message || recreateErr}`,
          };
        }
        const replaced = await this.hikvisionFacePresent(device, payload.person_id);
        if (replaced === false) {
          return {
            faceUploaded: false,
            faceVerified: false,
            error: `face upload failed: ${message}; the device still lists no face after recreating the user`,
          };
        }
        return { faceUploaded: true, faceVerified: replaced === true, error: null };
      }

      return { faceUploaded: false, faceVerified: false, error: `face upload failed: ${message}` };
    }

    let present = await this.hikvisionFacePresent(device, payload.person_id);
    if (present === false && device.vendor === 'hikvision') {
      try {
        await this.recreateHikvisionUserWithFace(device, deviceUserId, payload, timeout);
      } catch (err: any) {
        return {
          faceUploaded: false,
          faceVerified: false,
          error: `face missing after upload and user recreate failed: ${err?.message || err}`,
        };
      }
      present = await this.hikvisionFacePresent(device, payload.person_id);
    }

    if (present === false) {
      return {
        faceUploaded: true,
        faceVerified: false,
        error: 'face upload reported success but the device lists no face for this person (numOfFace = 0)',
      };
    }
    return { faceUploaded: true, faceVerified: present === true, error: null };
  }

  /**
   * Upload face photo after successful person sync
   * This is a separate operation that runs after SYNC_PERSON succeeds
   */
  private async uploadFacePhoto(
    device: DeviceInfo,
    employeeNo: string | number,  // Can be string (MD5) or number (device ID)
    personData: PersonData,
    timeout: number = 15000
  ): Promise<void> {
    console.log(' [Face Upload] === STARTING FACE UPLOAD ===');
    console.log(' [Face Upload] Employee No:', employeeNo);
    console.log(' [Face Upload] Person data:', {
      person_id: personData.person_id,
      first_name: personData.first_name,
      last_name: personData.last_name,
      identity_photo_url: personData.identity_photo_url,
      profile_picture_url: personData.profile_picture_url,
    });

    // Check if person has a photo
    const photoUrl = personData.identity_photo_url || personData.profile_picture_url;
    if (!photoUrl) {
      console.log(' [Face Upload]  No photo URL provided, skipping face upload');
      return;
    }

    console.log(' [Face Upload]  Photo URL found:', photoUrl);

    try {
      // Get vendor adapter
      const adapter = AdapterFactory.getAdapter(device.vendor);
      console.log(' [Face Upload]  Got adapter for vendor:', device.vendor);

      // Get full image URL
      const fullImageUrl = getFullImageUrl(photoUrl);
      console.log(' [Face Upload]  Full image URL:', fullImageUrl);

      // Fetch and convert image to base64
      console.log(' [Face Upload] Fetching image...');
      const fetchedPhotoBase64 = await fetchImageAsBase64(fullImageUrl);
      const photoBase64 = await resizeBase64Image(fetchedPhotoBase64, DEVICE_FACE_PHOTO_MAX_DIMENSION);
      console.log(' [Face Upload]  Image fetched and converted to base64, length:', photoBase64.length,
        '(was', fetchedPhotoBase64.length, 'before resize)');

      // Format face upload payload
      const payload = adapter.formatUploadFacePayload(employeeNo, photoBase64);
      console.log(' [Face Upload]  Payload formatted');
      console.log(' [Face Upload] Base64 preview:', {
        length: photoBase64.length,
        starts_with: photoBase64.substring(0, 50),
        has_data_uri: photoBase64.startsWith('data:'),
      });

      // Get endpoint and method
      let endpoint = adapter.getEndpoint(AdapterDeviceOperation.UPLOAD_FACE, device);
      const method = adapter.getMethod(AdapterDeviceOperation.UPLOAD_FACE);

      // Replace {FPID} placeholder with actual employeeNo (required by Hikvision FDLib URL)
      endpoint = endpoint.replace('{FPID}', encodeURIComponent(String(employeeNo)));

      // For Hikvision: delete existing face record before uploading.
      // FaceDataRecord POST fails silently when a face with the same FPID already exists.
      // Deleting first ensures the new photo always overwrites the old one.
      if (device.vendor === 'hikvision') {
        try {
          // Built from the shared resolver — this URL used to drop a port embedded in the
          // address (hikddns.com:1020) and target :80, so over DDNS the delete silently
          // went nowhere and the upload then failed with deviceUserAlreadyExistFace.
          const deleteUrl = deviceBaseUrl(
            device,
            '/ISAPI/Intelligent/FDLib/FDSearch/Delete?format=json&faceLibType=blackFD&FDID=1',
          );
          const deleteBody = { FPID: [{ value: String(employeeNo) }] };
          console.log(' [Face Upload] Deleting existing face record before upload:', deleteUrl);
          await (window.electronAPI!.deviceHttpRequest as any)({
            url: deleteUrl,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: deleteBody,
            timeout,
            digestAuth: { username: device.username, password: device.password },
            authFallback: 'digest',
          });
          console.log(' [Face Upload] Existing face record deleted (or did not exist)');
        } catch (delErr: any) {
          // Ignore — face may not exist yet, which is fine
          console.log(' [Face Upload] Face delete skipped/failed (ok):', delErr?.message);
        }
      }

      console.log(' [Face Upload] Sending request:', {
        endpoint,
        method,
        payloadSize: JSON.stringify(payload).length,
      });

      // Face upload always uses digest auth only — never session auth.
      // Reason: the FDLib multipart endpoint (for face terminals) does not require
      // session auth (newer firmware). For old firmware devices, session auth would
      // require AES-encrypting the body, which we don't implement. Digest auth works
      // for both old and new devices on the face upload endpoint.
      const requestHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      console.log(` [Face Upload] Using digest auth only for device ${device.id}`);

      const result = await window.electronAPI!.deviceHttpRequest({
        url: endpoint,
        method: method,
        headers: requestHeaders,
        body: payload,
        timeout,
        digestAuth: {
          username: device.username,
          password: device.password
        },
        authFallback: 'digest'
      } as any) as any;

      console.log(' [Face Upload] Request completed:', {
        success: result.success,
        error: result.error,
        hasData: !!result.data,
        statusCode: result.statusCode,
      });

      // CRITICAL: Log the actual response data to see if device returned errors
      console.log(' [Face Upload] Response data:', result.data);

      // Expand the results array to see individual results
      // A 200 whose BODY reports the item failed is still a failure. These per-item
      // results used to be console.log'd only, so a photo the device refused was
      // reported to the operator as synced.
      if (result.data && Array.isArray(result.data.results)) {
        const rejected = result.data.results.filter((r: any) => r?.success === false);
        if (rejected.length > 0) {
          const detail = rejected
            .map((r: any) => {
              const reason = Array.isArray(r.errors) && r.errors.length > 0
                ? r.errors.map((e: any) => (typeof e === 'string' ? e : JSON.stringify(e))).join(', ')
                : 'motivo não informado';
              return `user ${r.user_id}: ${reason}`;
            })
            .join('; ');
          throw new Error(`device rejected the photo — ${detail}`);
        }
      }

      if (result.success) {
        console.log(' [Face Upload] Photo uploaded successfully!');
      } else {
        const statusCode = result.statusCode ? ` (HTTP ${result.statusCode})` : '';
        const deviceMsg = result.data
          ? typeof result.data === 'string'
            ? result.data.substring(0, 200)
            : JSON.stringify(result.data).substring(0, 200)
          : '';
        const errDetail = deviceMsg ? `${result.error} — ${deviceMsg}` : result.error;
        console.error(' [Face Upload] Photo upload failed:', errDetail);
        throw new Error(`Face upload failed${statusCode}: ${errDetail}`);
      }
    } catch (error) {
      console.error(' [Face Upload] Exception during photo upload:', error);
      console.error(' [Face Upload] Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      // Don't throw - face upload failure shouldn't fail the entire sync
      throw error; // Re-throw so the calling code's catch block can see it
    }
  }

  /**
   * Check if we should use adapter-based formatting
   */
  private shouldUseAdapter(operation: DeviceOperation): boolean {
    // Use adapters for sync, remove, load, and door open operations
    return operation.type === 'sync_person' ||
           operation.type === 'remove_person' ||
           operation.type === 'load_people' ||
           operation.type === 'open_door';
  }

  /**
   * Map legacy operation to adapter operation enum
   */
  private mapToAdapterOperation(operation: DeviceOperation): AdapterDeviceOperation {
    switch (operation.type) {
      case 'sync_person':
        return AdapterDeviceOperation.SYNC_PERSON;
      case 'remove_person':
        return AdapterDeviceOperation.REMOVE_PERSON;
      case 'check_health':
        return AdapterDeviceOperation.CHECK_HEALTH;
      case 'load_people':
        return AdapterDeviceOperation.LOAD_PEOPLE;
      case 'open_door':
        return AdapterDeviceOperation.OPEN_DOOR;
      default:
        return AdapterDeviceOperation.SYNC_PERSON;
    }
  }

  /**
   * Cloud backend communication (always available)
   */
  private async cloudOperation(
    device: DeviceInfo,
    operation: DeviceOperation,
    payload: any,
    startTime: number,
    attempts: DeviceOperationResult['attempts']
  ): Promise<DeviceOperationResult> {
    // OPEN_DOOR uses vendor-specific IoT Connect endpoints
    let endpoint: string;
    let cloudPayload = payload;
    if (operation.type === 'open_door') {
      const isRFID = device.device_type === 'controlid_rfid' || device.device_type === 'rfid_reader';
      endpoint = isRFID
        ? `iot-connect/rfid/devices/${device.id}/gate/open`
        : `iot-connect/facial-recognition/devices/${device.id}/door/open`;
      cloudPayload = { gate_id: 1 };
    } else {
      endpoint = this.buildCloudEndpoint(device, operation);
    }

    logger.debug('deviceComm', 'Making cloud request', {
      endpoint,
      method: operation.method,
    });

    try {
      const response = await api.post(endpoint, cloudPayload, { _silentOnError: true } as any);

      attempts.push({
        address: 'cloud',
        result: 'success',
        latencyMs: Date.now() - startTime,
      });

      return {
        success: true,
        data: response.data,
        mode: CommunicationMode.CLOUD,
        latencyMs: Date.now() - startTime,
        source: 'cloud',
        attempts,
      };
    } catch (error: any) {
      logger.warn('deviceComm', 'Cloud operation failed', { endpoint, error: String(error) });
      attempts.push({
        address: 'cloud',
        result: 'error',
        error: String(error),
        latencyMs: Date.now() - startTime,
      });

      return {
        success: false,
        error: error.message || 'Cloud operation failed',
        mode: CommunicationMode.CLOUD,
        latencyMs: Date.now() - startTime,
        source: 'cloud',
        attempts,
      };
    }
  }

  /**
   * Build device endpoint URL (for direct communication)
   */
  private buildDeviceEndpoint(device: DeviceInfo, operation: DeviceOperation): string {
    let ip = device.ip_address || device.local_ip_address;
    if (!ip) {
      throw new Error('No IP address available for device');
    }

    console.log(' [Build URL] Input:', {
      originalIp: ip,
      devicePort: device.port,
      vendor: device.vendor,
    });

    // Strip any existing protocol (http:// or https://)
    // Database stores IPs like "http://192.168.0.210:8008"
    ip = ip.replace(/^https?:\/\//, '');

    console.log(' [Build URL] After protocol strip:', ip);

    // Parse port from IP if it includes port (e.g., "hikddns.com:1020" or "192.168.0.210:8008")
    let deviceIp = ip;
    let port = device.port || '80';

    if (ip.includes(':')) {
      const parts = ip.split(':');
      deviceIp = parts[0];
      port = parts[1];
    }

    const protocol = port === '443' ? 'https' : 'http';

    console.log(' [Build URL] Parsed:', {
      deviceIp,
      port,
      protocol,
    });

    let finalUrl: string;

    switch (device.vendor) {
      case 'hikvision':
        if (!operation.hikEndpoint) {
          throw new Error('Hikvision endpoint not defined for operation');
        }
        finalUrl = `${protocol}://${deviceIp}:${port}/ISAPI/${operation.hikEndpoint}?format=json`;
        break;

      case 'controlid':
        if (!operation.ctrlEndpoint) {
          throw new Error('ControlID endpoint not defined for operation');
        }
        finalUrl = `${protocol}://${deviceIp}:${port}/${operation.ctrlEndpoint}`;
        break;

      case 'rfid':
        if (!operation.rfidEndpoint) {
          throw new Error('RFID endpoint not defined for operation');
        }
        finalUrl = `${protocol}://${deviceIp}:${port}/api/${operation.rfidEndpoint}`;
        break;

      default:
        throw new Error(`Unsupported vendor: ${device.vendor}`);
    }

    console.log(' [Build URL] Final URL:', finalUrl);
    return finalUrl;
  }

  /**
   * Build cloud endpoint URL (for backend communication)
   */
  private buildCloudEndpoint(device: DeviceInfo, operation: DeviceOperation): string {
    return `/devices/${device.id}/${operation.cloudEndpoint}`;
  }

  /**
   * Log metrics for debugging
   */
  private log(level: 'debug' | 'info' | 'warn' | 'error', message: string, data?: any) {
    if (!this.metricsEnabled && level === 'debug') return;

    const prefix = '[DeviceComm]';
    const logData = data ? { ...data } : undefined;

    switch (level) {
      case 'debug':
        logger.debug('deviceComm', message, logData);
        break;
      case 'info':
        logger.info('deviceComm', message, logData);
        break;
      case 'warn':
        logger.warn('deviceComm', message, logData);
        break;
      case 'error':
        logger.error('deviceComm', message, logData);
        break;
    }
  }

  // ==================== CONTROLID ACCESS SETUP ====================

  /**
   * Make an authenticated HTTP request to a ControlID device.
   * Reuses the cached session from SessionManager — no extra login needed.
   */
  private async controlIDRequest(
    device: DeviceInfo,
    path: 'create_objects.fcgi' | 'modify_objects.fcgi' | 'load_objects.fcgi' | 'destroy_objects.fcgi' | 'user_set_image_list.fcgi' | 'execute_actions.fcgi',
    payload: any,
    timeout: number
  ): Promise<any> {
    const adapter = AdapterFactory.getAdapter('controlid');
    const sessionInfo = await sessionManager.getOrCreateSession(device, adapter);

    if (!sessionInfo) {
      throw new Error('ControlID session auth failed — could not establish session with device');
    }

    const ip = device.ip_address || device.local_ip_address;
    if (!ip) throw new Error('No IP address for device');

    const cleanIp = ip.replace(/^https?:\/\//, '');
    let deviceIp = cleanIp;
    let port = device.port || '80';
    if (cleanIp.includes(':')) {
      const [h, p] = cleanIp.split(':');
      deviceIp = h;
      port = p;
    }

    const protocol = port === '443' ? 'https' : 'http';
    const url = `${protocol}://${deviceIp}:${port}/${path}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json; charset=utf-8',
      'Accept': 'application/json',
    };
    if (sessionInfo) {
      Object.assign(headers, sessionInfo.headers);
    }

    // Retry on transient network failures (connection reset, timeout, etc.).
    // Logic errors from the device (constraint violations, bad data) throw immediately.
    const MAX_RETRIES = 2;
    const isTransientMsg = (msg: string) =>
      /timeout|ECONNRESET|ECONNREFUSED|ETIMEDOUT|network|socket|connection/i.test(msg);

    let sessionRefreshed = false;
    let lastError: Error = new Error(`ControlID ${path} request failed`);
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      if (attempt > 0) {
        const delayMs = 600 * attempt; // 600ms, then 1200ms
        console.warn(`[ControlID] ${path} retrying (attempt ${attempt + 1}/${MAX_RETRIES + 1}) after ${delayMs}ms`);
        await new Promise(r => setTimeout(r, delayMs));
      }

      const result = await (window as any).electronAPI!.deviceHttpRequest({
        url,
        method: 'POST',
        headers,
        body: payload,
        timeout,
      }) as any;

      if (!result.success) {
        const errMsg = String(result.error || 'unknown');
        lastError = new Error(`ControlID ${path} failed: ${errMsg}`);

        // Cached session went stale/invalid (e.g. the device's own admin web UI was opened
        // directly and evicted it — ControlID firmware only keeps one active session at a
        // time). Refresh the session and retry once instead of failing outright.
        if (result.statusCode === 401 && !sessionRefreshed) {
          sessionRefreshed = true;
          console.warn(`[ControlID] ${path} got 401 (invalid session) — refreshing session and retrying`);
          sessionManager.invalidateSession(device);
          const freshSession = await sessionManager.getOrCreateSession(device, adapter);
          if (freshSession) {
            Object.assign(headers, freshSession.headers);
            if (attempt < MAX_RETRIES) continue;
          }
        }

        if (isTransientMsg(errMsg) && attempt < MAX_RETRIES) continue;
        throw lastError;
      }

      const data = typeof result.data === 'string'
        ? (() => { try { return JSON.parse(result.data); } catch { return result.data; } })()
        : result.data;

      // ControlID returns HTTP 200 but with an error field on failure
      if (data && typeof data === 'object' && data.error) {
        throw new Error(`ControlID ${path} error: ${data.error}`);
      }

      return data;
    }
    throw lastError;
  }

  /**
   * Load all users and cards from a ControlID iDUHF device via direct Electron connection.
   * Returns registrations (vehicle UUID or plate) so callers can mark vehicles as is_on_device.
   * Returns empty array if not in Electron mode or device has no IP.
   */
  async loadControlIDDeviceCards(device: DeviceInfo, timeout = 15000): Promise<Array<{ registration: string; cardValue: string }>> {
    if (!(window as any).electronAPI?.deviceHttpRequest) return [];
    const ip = device.local_ip_address || device.ip_address;
    if (!ip) return [];
    try {
      const [cardsResp, usersResp] = await Promise.all([
        this.controlIDRequest(device, 'load_objects.fcgi', { object: 'cards' }, timeout),
        this.controlIDRequest(device, 'load_objects.fcgi', { object: 'users' }, timeout),
      ]);
      const cards: any[] = cardsResp?.cards || [];
      const users: any[] = usersResp?.users || [];
      const userMap = new Map(users.map((u: any) => [u.id as number, u]));
      return cards.map((c: any) => ({
        registration: (userMap.get(c.user_id)?.registration as string) || '',
        cardValue: String(c.value),
      }));
    } catch (err: any) {
      console.warn('[ControlID] loadControlIDDeviceCards failed:', err?.message);
      return [];
    }
  }

  /**
   * Fetch fingerprint templates directly from a ControlID device over the local network
   * (Electron only). Mirrors iot-connect's ControlIDFingerprintOps.FetchFingerprintsFromDevice:
   * lookup the device's integer user_id by registration (personCode), then load its templates.
   *
   * Devices that are only reachable on the local LAN (no route from the cloud iot-connect
   * service) can ONLY be reached this way — returns null (not []) when direct communication
   * isn't possible at all, so callers can distinguish "not attempted" from "attempted, found nothing".
   */
  async fetchControlIDFingerprintsDirect(
    device: DeviceInfo,
    personCode: string,
    forceDeviceUserID?: number,
    timeout = 15000,
  ): Promise<Array<{ finger_type: number; template_b64: string }> | null> {
    if (!(window as any).electronAPI?.deviceHttpRequest) return null;
    const ip = device.local_ip_address || device.ip_address;
    if (!ip) return null;

    // controlIDRequest/sessionManager/the ControlID adapter all resolve IP as
    // `ip_address || local_ip_address` (cloud-first) — the opposite of what a LAN-direct
    // fetch needs. Force ip_address to the resolved local-preferring address and clear
    // local_ip_address so every downstream consumer is pinned to it. Same pattern as
    // loadExistingDeviceUsers's DIRECT-mode call above.
    const directDevice: DeviceInfo = { ...device, ip_address: ip, local_ip_address: undefined };

    try {
      let deviceUserID = forceDeviceUserID ?? 0;
      if (!deviceUserID) {
        const lookupResp = await this.controlIDRequest(directDevice, 'load_objects.fcgi', {
          object: 'users',
          where: { users: { registration: [personCode] } },
        }, timeout);
        const users: any[] = lookupResp?.users || [];
        if (users.length === 0) {
          this.log('info', 'fetchControlIDFingerprintsDirect: person not found on device', { deviceId: device.id, personCode });
          return [];
        }
        deviceUserID = users[0].id;
      }

      const templatesResp = await this.controlIDRequest(directDevice, 'load_objects.fcgi', {
        object: 'templates',
        where: { templates: { user_id: [deviceUserID] } },
      }, timeout);
      const templates: any[] = templatesResp?.templates || [];
      return templates
        .filter((t: any) => !!t.template)
        .map((t: any) => ({ finger_type: t.finger_type, template_b64: t.template }));
    } catch (err: any) {
      console.warn('[ControlID] fetchControlIDFingerprintsDirect failed:', err?.message);
      throw err;
    }
  }

  /**
   * Parse an RFID tag string to a numeric card value for ControlID.
   * Handles hex (with/without 0x prefix), decimal, and explicit Wiegand representations.
   *
   *  "003D4C3"    → parseInt("003D4C3", 16) = 251075     (has A-F → hex → decimal)
   *  "0253985"    → parseInt("0253985", 16) = 2439557    (leading 0 = padded hex → decimal)
   *  "0x25E31E"   → parseInt("25E31E", 16) = 2482974    (0x prefix → hex → decimal)
   *  "003D4C3"    → hex 251075 → Wiegand fc=3,   cn=54467 → 3*4294967296+54467
   *  "0253985"    → hex 2439557 → Wiegand fc=37,  cn=14725 → 37*4294967296+14725
   *  "25E33F"     → hex 2483007 → Wiegand fc=37,  cn=63295 → 37*4294967296+63295
   *  "3,54467"    → explicit Wiegand input         → 3*4294967296+54467
   *
   * CRITICAL — DO NOT REMOVE WIEGAND ENCODING.
   * ControlID iDUHF devices require Wiegand (fc<<32|cn) card values.
   * Using raw decimal broke RFID access for many vehicles; this encoding was the deliberate fix.
   */
  private parseRfidTagToCardValue(tag: string, format: RfidTagFormat = 'auto'): number | null {
    // An explicitly recorded format ends the guessing: the operator stated how the number is
    // written, so there is nothing left to infer. `auto` falls through to the legacy rules
    // below, which every tag stored before vehicles.rfid_tag_format existed still relies on.
    //
    // This also fixes the 40-bit case the legacy path cannot express: a ControlID ID is already
    // the final card value, and re-encoding it with JS 32-bit bitwise operators silently
    // dropped the facility code (21474843585 became card 7105).
    if (format !== 'auto') {
      return toCardValue(tag, format);
    }

    const t = (tag || '').trim();
    if (!t) return null;

    // Explicit Wiegand "area,card" format already provided by caller
    if (t.includes(',')) {
      const parts = t.split(',');
      if (parts.length === 2) {
        const fc = parseInt(parts[0].trim(), 10);
        const cn = parseInt(parts[1].trim(), 10);
        if (!isNaN(fc) && !isNaN(cn) && cn >= 0) return fc * 4294967296 + cn;
      }
      return null;
    }

    // Step 1 — parse the stored hex/decimal tag string to a raw integer
    const has0xPrefix = /^0[xX]/.test(t);
    const hasHexLetters = /[a-fA-F]/.test(t);
    // Leading-zero strings are always hex (RFID readers pad hex values, e.g. "003D4C3", "0253985")
    const hasLeadingZero = t.length > 1 && t.startsWith('0') && !has0xPrefix;

    let dec: number;
    if (has0xPrefix || hasHexLetters || hasLeadingZero) {
      const hexStr = has0xPrefix ? t.slice(2) : t;
      dec = parseInt(hexStr, 16);
    } else {
      dec = parseInt(t, 10);
    }
    if (isNaN(dec) || dec <= 0) return null;

    // Step 2 — encode as ControlID Wiegand (Código e Área):
    //   stored value = (fc << 32) | cn  where fc = dec >> 16, cn = dec & 0xFFFF
    // The iDUHF antenna sends this Wiegand-encoded value to the ControlID database.
    const fc = (dec >> 16) & 0xFFFF;
    const cn = dec & 0xFFFF;
    return fc * 4294967296 + cn;
  }

  /**
   * Returns the raw decimal integer for an rfid_tag hex string (no Wiegand encoding).
   * Used only as a fallback when looking for cards created before the Wiegand encoding fix
   * (legacy cards stored as raw decimal on the device).
   */
  private parseRfidTagRawDecimal(tag: string): number | null {
    const t = (tag || '').trim();
    if (!t || t.includes(',')) return null;
    const has0xPrefix = /^0[xX]/.test(t);
    const hasHexLetters = /[a-fA-F]/.test(t);
    const hasLeadingZero = t.length > 1 && t.startsWith('0') && !has0xPrefix;
    let dec: number;
    if (has0xPrefix || hasHexLetters || hasLeadingZero) {
      dec = parseInt(has0xPrefix ? t.slice(2) : t, 16);
    } else {
      dec = parseInt(t, 10);
    }
    return isNaN(dec) || dec <= 0 ? null : dec;
  }

  /**
   * ControlID-specific SYNC_PERSON: check if user exists first, then create or update.
   * Avoids duplicates when the same person is synced multiple times.
   * Also handles photo upload with the correct device user ID.
   */
  private async syncControlIDPerson(
    device: DeviceInfo,
    payload: any,
    startTime: number,
    attempts: DeviceOperationResult['attempts'],
    source: OperationSource,
    timeout: number,
  ): Promise<DeviceOperationResult> {
    const registration = payload.person_id;
    const fullName = [(payload.first_name || '').trim(), (payload.last_name || '').trim()]
      .filter(Boolean).join(' ');

    try {
      // Handle revoke: isAllowed=false means remove this user from the device.
      // Called by vehicleRfidSync when a vehicle RFID tag is revoked.
      if (payload.isAllowed === false) {
        const existingResp = await this.controlIDRequest(device, 'load_objects.fcgi', {
          object: 'users',
          where: { users: { registration: [registration] } },
        }, timeout);
        const existingUser: any = existingResp?.users?.[0];
        if (!existingUser) {
          console.log(`[ControlID] Revoke: user ${registration} not on device — already removed`);
          attempts.push({ address: device.ip_address || device.local_ip_address || 'unknown', result: 'success', latencyMs: Date.now() - startTime });
          return { success: true, data: { message: 'already removed' }, mode: CommunicationMode.DIRECT, latencyMs: Date.now() - startTime, source, attempts };
        }
        const numericId: number = existingUser.id;
        // Delete cards first — device may not cascade automatically
        try {
          await this.controlIDRequest(device, 'destroy_objects.fcgi', {
            object: 'cards',
            where: { cards: { user_id: [numericId] } },
          }, timeout);
          console.log(`[ControlID] Revoke: deleted cards for user id=${numericId}`);
        } catch { /* non-fatal: user may have no cards */ }
        // Delete user
        await this.controlIDRequest(device, 'destroy_objects.fcgi', {
          object: 'users',
          where: { users: { id: [numericId] } },
        }, timeout);
        console.log(`[ControlID] Revoked user id=${numericId} (${registration})`);
        attempts.push({ address: device.ip_address || device.local_ip_address || 'unknown', result: 'success', latencyMs: Date.now() - startTime });
        return { success: true, data: { deleted: numericId }, mode: CommunicationMode.DIRECT, latencyMs: Date.now() - startTime, source, attempts };
      }

      // 0. Download face photo upfront so it can be included in create_objects.fcgi
      // The Go backend (controlid_face_adapter.go) puts "image" directly in the user payload —
      // this is the only reliable way to set/update the face on ControlID devices.
      let faceImageBase64: string | undefined;
      let facePhotoError: string | null = null;
      const facePhotoUrl = payload.identity_photo_url || payload.profile_picture_url;
      if (facePhotoUrl) {
        try {
          const fullImageUrl = getFullImageUrl(facePhotoUrl);
          const fetchedFaceImageBase64 = await fetchImageAsBase64(fullImageUrl);
          faceImageBase64 = await resizeBase64Image(fetchedFaceImageBase64, DEVICE_FACE_PHOTO_MAX_DIMENSION);
          console.log(` [ControlID] Photo ready for enrollment: ${faceImageBase64.length} bytes (was ${fetchedFaceImageBase64.length} before resize)`);
        } catch (photoErr: any) {
          // A provided-but-unfetchable photo is a face-credential failure, not a
          // silent no-face enrollment — tracked and surfaced in the final result.
          facePhotoError = `face photo download failed: ${photoErr?.message || photoErr}`;
          console.warn(` [ControlID] Could not download photo:`, photoErr?.message);
        }
      }

      // 1. Check if user already exists on device by registration UUID
      const existingResp = await this.controlIDRequest(device, 'load_objects.fcgi', {
        object: 'users',
        where: { users: { registration: [registration] } },
      }, timeout);
      const existingUser: any = existingResp?.users?.[0];

      // 2. Build user values — include id to trigger UPDATE, omit to CREATE
      const expiresDate = new Date();
      expiresDate.setFullYear(expiresDate.getFullYear() + 10);
      const expiresTimestamp = Math.floor(expiresDate.getTime() / 1000);

      // Deterministic numeric id ensures the same person gets the same "código" on every device.
      const deterministicId = uuidToDeterministicControlIDId(registration);
      // Build name: include vehicle plate(s) so the user is searchable on the device.
      // e.g. "Gaspar Andrade — GEA7E04" or just "GEA7E04" if person name is empty.
      const plates: string[] = payload.vehicle_plates || (payload.vehicle_plate ? [payload.vehicle_plate] : []);
      const platesSuffix = plates.length > 0 ? ` — ${plates.join(', ')}` : '';
      const nameToUse = fullName
        ? `${fullName}${platesSuffix}`
        : (plates.length > 0 ? plates.join(', ') : `Usuário ${registration.slice(0, 8)}`);
      const userValue: any = {
        registration,
        name: nameToUse,
        // NOTE: department_id is NOT a column in the users table on newer ControlID firmware.
        // Department assignment is done separately via user_departments after user creation.
        expires: expiresTimestamp,
      };

      if (existingUser) {
        userValue.id = existingUser.id;
      } else {
        userValue.id = deterministicId;
      }

      // 3. Create or update user on device
      let deviceUserId: number;

      if (existingUser) {
        // User already exists — update via modify_objects.fcgi.
        // IMPORTANT: do NOT destroy+recreate — destroy_objects cascades and deletes
        // fingerprints (fingers table) linked to this user, wiping enrolled biometrics.
        // create_objects.fcgi with an existing id throws a unique-constraint error on ControlID.
        console.log(`[ControlID] Updating existing user id=${existingUser.id} via modify_objects — "${fullName}"`);
        const { id: _id, ...updateFields } = userValue; // exclude id from values; use where instead
        try {
          await this.controlIDRequest(device, 'modify_objects.fcgi', {
            object: 'users',
            values: updateFields,
            where: { users: { id: [existingUser.id] } },
          }, timeout);
        } catch (modifyErr: any) {
          // modify_objects.fcgi may be unsupported on older firmware — log and continue.
          // The user already exists on the device so access will still work.
          console.warn(`[ControlID] modify_objects.fcgi failed (older firmware?), user still exists:`, modifyErr?.message);
        }
        deviceUserId = existingUser.id;
        console.log(`[ControlID] Updated user id=${deviceUserId} with photo=${!!faceImageBase64}`);
      } else {
        // New user — create with deterministic ID
        let syncResp: any;
        try {
          syncResp = await this.controlIDRequest(device, 'create_objects.fcgi', {
            object: 'users',
            values: [userValue],
          }, timeout);
          deviceUserId = syncResp?.ids?.[0] ?? deterministicId;
          console.log(` [ControlID] Created user id=${deviceUserId}`);
        } catch (createErr: any) {
          // If UNIQUE constraint on ID, another sync path already created this user
          // with the same deterministic ID — load it and update instead
          if (String(createErr?.message).toLowerCase().includes('unique') ||
              String(createErr?.message).toLowerCase().includes('constraint')) {
            console.warn(`[ControlID] Create failed (ID conflict), falling back to modify for id=${deterministicId}`);
            const conflictResp = await this.controlIDRequest(device, 'load_objects.fcgi', {
              object: 'users',
              where: { users: { id: [deterministicId] } },
            }, timeout);
            const conflictUser = conflictResp?.users?.[0];
            if (conflictUser) {
              // Update via modify_objects (no destroy — would cascade-delete fingerprints)
              const { id: _id, ...conflictUpdateFields } = userValue;
              try {
                await this.controlIDRequest(device, 'modify_objects.fcgi', {
                  object: 'users',
                  values: [conflictUpdateFields],
                  where: { users: { id: [conflictUser.id] } },
                }, timeout);
              } catch {
                // Non-fatal — user exists, continue
              }
              deviceUserId = conflictUser.id;
              console.log(`[ControlID] Recovered: updated user id=${deviceUserId} via modify after ID conflict`);
            } else {
              throw createErr;
            }
          } else {
            throw createErr;
          }
        }
      }

      if (!deviceUserId) {
        return {
          success: false,
          error: 'Failed to get device user ID after ControlID sync',
          mode: CommunicationMode.DIRECT,
          latencyMs: Date.now() - startTime,
          source,
          attempts,
        };
      }

      // 3b. Upload face photo separately (users table has no image column)
      if (faceImageBase64 && deviceUserId) {
        try {
          const timestamp = Math.floor(Date.now() / 1000);
          const imageResp = await this.controlIDRequest(device, 'user_set_image_list.fcgi', {
            user_images: [{ user_id: deviceUserId, timestamp, image: faceImageBase64 }],
          }, timeout);

          // A 200 whose BODY reports the item failed is still a failure. These per-item
          // results were only logged, so a photo the device refused — a face it could not
          // decode or model — was reported to the operator as synced.
          const rejected = Array.isArray(imageResp?.results)
            ? imageResp.results.filter((r: any) => r?.success === false)
            : [];
          if (rejected.length > 0) {
            const detail = rejected
              .map((r: any) => {
                const reason = Array.isArray(r.errors) && r.errors.length > 0
                  ? r.errors.map((e: any) => (typeof e === 'string' ? e : JSON.stringify(e))).join(', ')
                  : 'motivo não informado';
                return `user ${r.user_id}: ${reason}`;
              })
              .join('; ');
            throw new Error(`device rejected the photo — ${detail}`);
          }

          console.log(`[ControlID] Face photo uploaded for user id=${deviceUserId}`);
        } catch (photoErr: any) {
          facePhotoError = `face upload failed: ${photoErr?.message || photoErr}`;
          console.warn(`[ControlID] Face photo upload failed for user id=${deviceUserId}:`, photoErr?.message);
        }
      }

      // 3c. Assign user to default access group — populates "Nº de Departamentos" in ControlID UI.
      // Works by loading the first available group and creating a user_groups entry.
      // user_departments is also attempted as a fallback for older firmware.
      try {
        const groupsResp = await this.controlIDRequest(device, 'load_objects.fcgi', {
          object: 'groups',
          limit: 1,
        }, timeout);
        const groups: any[] = groupsResp?.groups || [];
        if (groups.length > 0) {
          const groupId = groups[0].id;
          await this.controlIDRequest(device, 'create_objects.fcgi', {
            object: 'user_groups',
            values: [{ user_id: deviceUserId, group_id: groupId }],
          }, timeout);
          console.log(`[ControlID] Assigned user id=${deviceUserId} to group id=${groupId}`);
        } else {
          console.warn(`[ControlID] No groups found on device — skipping group assignment`);
        }
      } catch (groupErr: any) {
        console.warn(`[ControlID] Could not assign group for user id=${deviceUserId}:`, groupErr?.message);
      }

      // Also try user_departments for older firmware that uses it (best-effort, non-fatal).
      try {
        await this.controlIDRequest(device, 'create_objects.fcgi', {
          object: 'user_departments',
          values: [{ user_id: deviceUserId, department_id: 1 }],
        }, timeout);
        console.log(`[ControlID] Assigned user id=${deviceUserId} to department 1 (Padrão)`);
      } catch {
        // Non-fatal: newer firmware doesn't have user_departments table
      }

      // 4. Register RFID cards (vehicle tags) on device
      const rfidTags: string[] = payload.rfid_tags || [];
      console.log(`[ControlID] Step 4: rfid_tags payload=`, rfidTags, `vehicle_plates=`, payload.vehicle_plates);
      // A tag that does not reach the reader MUST fail the sync. These errors used to be
      // caught and console.warn'd, so a resident whose tag never landed was still told
      // "sincronizado" — the gate then does not open and nothing records why.
      const failedTags: string[] = [];
      if (rfidTags.length > 0) {
        let existingValues = new Set<number>();
        try {
          const existingCardsResp = await this.controlIDRequest(device, 'load_objects.fcgi', {
            object: 'cards',
            where: { cards: { user_id: [deviceUserId] } },
          }, timeout);
          existingValues = new Set<number>((existingCardsResp?.cards || []).map((c: any) => Number(c.value)));
        } catch (listErr: any) {
          // Cannot tell what is already there — every requested tag is unconfirmed.
          console.warn(` [ControlID] Could not list existing cards:`, listErr?.message);
          failedTags.push(...rfidTags.filter(Boolean).map(t => `${t} (não foi possível ler os cartões do leitor)`));
        }

        if (failedTags.length === 0) {
          for (const rawTag of rfidTags) {
            if (!rawTag) continue;

            const cardValue = this.parseRfidTagToCardValue(rawTag);
            if (cardValue === null) {
              // Previously skipped with a warning, which read as success to the caller.
              failedTags.push(`${rawTag} (valor de tag inválido)`);
              continue;
            }
            if (existingValues.has(cardValue)) {
              console.log(` [ControlID] Card already registered: "${rawTag}" (${cardValue}) for user ${deviceUserId}`);
              continue;
            }

            try {
              await this.controlIDRequest(device, 'create_objects.fcgi', {
                object: 'cards',
                values: [{ value: cardValue, user_id: deviceUserId }],
              }, timeout);
            } catch (cardErr: any) {
              failedTags.push(`${rawTag} (${cardErr?.message || 'erro no dispositivo'})`);
              continue;
            }

            // Read back: an accepted write that does not show up did not stick, and
            // trusting the ack alone is how a tag silently goes missing.
            try {
              const verifyResp = await this.controlIDRequest(device, 'load_objects.fcgi', {
                object: 'cards',
                where: { cards: { user_id: [deviceUserId] } },
              }, timeout);
              const storedValues = new Set<number>((verifyResp?.cards || []).map((c: any) => Number(c.value)));
              if (!storedValues.has(cardValue)) {
                failedTags.push(`${rawTag} (gravada, mas ausente na releitura do leitor)`);
                continue;
              }
              existingValues = storedValues;
            } catch (verifyErr: any) {
              failedTags.push(`${rawTag} (não foi possível confirmar no leitor: ${verifyErr?.message || 'erro'})`);
              continue;
            }

            console.log(` [ControlID] Registered RFID card: "${rawTag}" → ${cardValue} for user ${deviceUserId}`);
          }
        }
      }

      if (failedTags.length > 0) {
        const detail = failedTags.join('; ');
        console.warn(` [ControlID] RFID card registration failed for user ${deviceUserId}: ${detail}`);
        return {
          success: false,
          error: `tag não registrada no leitor: ${detail}`,
          mode: CommunicationMode.DIRECT,
          latencyMs: Date.now() - startTime,
          source,
          attempts,
        };
      }

      // 5. Push fingerprint templates
      // Payload includes fingerprint_templates when called from BiometriaTab sync.
      const fpTemplates: Array<{ templateB64: string }> = payload.fingerprint_templates || [];
      let fpPushed = 0;
      let fpFailed = 0;
      let fpLastError = '';
      if (fpTemplates.some(fp => fp.templateB64)) {
        // Delete all existing templates first — create_objects on "templates" always INSERTs,
        // so without this each sync adds duplicate rows.
        try {
          await this.controlIDRequest(device, 'destroy_objects.fcgi', {
            object: 'templates',
            where: { templates: { user_id: [deviceUserId] } },
          }, timeout);
          console.log(`[ControlID] Cleared existing templates for user id=${deviceUserId}`);
        } catch (delErr: any) {
          console.warn(`[ControlID] Failed to clear existing templates (continuing):`, delErr?.message);
        }
      }
      for (const fp of fpTemplates) {
        if (!fp.templateB64) continue;
        console.log(`[ControlID] Pushing fingerprint template for user id=${deviceUserId}, template length=${fp.templateB64.length}, prefix=${fp.templateB64.slice(0, 16)}`);
        try {
          const pushResp = await this.controlIDRequest(device, 'create_objects.fcgi', {
            object: 'templates',
            values: [{ user_id: deviceUserId, finger_type: 0, template: fp.templateB64 }],
          }, timeout);
          fpPushed++;
          console.log(`[ControlID]  Pushed fingerprint template for user id=${deviceUserId}, response:`, JSON.stringify(pushResp));
        } catch (fpErr: any) {
          fpFailed++;
          fpLastError = fpErr?.message || 'unknown';
          console.error(`[ControlID]  Could not push fingerprint template for user id=${deviceUserId}:`, fpErr?.message);
        }
      }

      attempts.push({
        address: device.ip_address || device.local_ip_address || 'unknown',
        result: 'success',
        latencyMs: Date.now() - startTime,
      });

      // A facial credential without a face is NOT synced: when a photo was
      // provided but never confirmed on the device, the sync is a failure —
      // cards/fingerprints above were still registered, so retrying is cheap
      // and idempotent.
      if (facePhotoError) {
        return {
          success: false,
          error: `user synced but ${facePhotoError}`,
          data: { ids: [deviceUserId] },
          faceUploaded: false,
          mode: CommunicationMode.DIRECT,
          latencyMs: Date.now() - startTime,
          source,
          attempts,
        };
      }

      return {
        success: true,
        faceUploaded: facePhotoUrl ? true : undefined,
        data: {
          ids: [deviceUserId],
          fpPushed,
          fpFailed,
          fpLastError: fpFailed > 0 ? fpLastError : undefined,
        },
        mode: CommunicationMode.DIRECT,
        latencyMs: Date.now() - startTime,
        source,
        attempts,
      };
    } catch (err: any) {
      attempts.push({
        address: device.ip_address || device.local_ip_address || 'unknown',
        result: 'error',
        error: String(err),
        latencyMs: Date.now() - startTime,
      });
      return {
        success: false,
        error: err?.message || 'ControlID sync failed',
        mode: CommunicationMode.DIRECT,
        latencyMs: Date.now() - startTime,
        source,
        attempts,
      };
    }
  }

  /**
   * ControlID-specific REMOVE_PERSON: load user by registration UUID → delete by numeric id.
   * This is more reliable than deleting by registration field because:
   * - Some ControlID firmware doesn't support filtering destroy_objects.fcgi by registration
   * - Users created by the backend may have a different numeric ID than deterministicId
   */
  private async removeControlIDPerson(
    device: DeviceInfo,
    payload: any,
    startTime: number,
    attempts: DeviceOperationResult['attempts'],
    source: OperationSource,
    timeout: number,
  ): Promise<DeviceOperationResult> {
    // Accept either person_id (UUID) or devicePersonId (numeric device id or UUID)
    const rawIdentifier = payload.person_id ?? payload.devicePersonId;
    const identifier = rawIdentifier === undefined || rawIdentifier === null
      ? ''
      : String(rawIdentifier).trim();
    if (!identifier) {
      return {
        success: false,
        error: 'removeControlIDPerson: person_id or devicePersonId required',
        mode: CommunicationMode.DIRECT,
        latencyMs: Date.now() - startTime,
        source,
        attempts,
      };
    }

    // The identifier is NOT always a registration. ZonesDevices' "Pessoas no
    // Dispositivo" list prefers the ControlID numeric user id (load_objects' `id`)
    // because that is the only identifier device-only enrollments have at all —
    // eCondos-era users whose registration reads "user:65cfa274…", and users with
    // no registration whatsoever. Looking a numeric id up as a registration can
    // never match, and the miss used to be reported as "already removed" success:
    // the toast said removed and the face stayed on the reader.
    //
    // So pick the lookup key from the identifier's shape, and for an all-digits
    // identifier still try registration afterwards — a registration may legitimately
    // hold digits (an employee number written by another system). Mirrors the cloud
    // path in adapters/controlid/controlid_face_adapter.go DeletePerson.
    // A UUID identifier (the auto paths: exit detection, deferred-removal queue,
    // expired visitors, PersonDetailModal) is likewise not always a registration.
    // The same person can sit on the device under a DERIVED numeric id when the
    // registration was never written or got lost — see fetchControlIDFingerprints'
    // long-standing Strategy 1/2/3 lookup:
    //   registration = UUID    → Electron direct sync (syncControlIDPerson)
    //   id = FNV-1a(UUID)      → Go backend sync (controlid_face_adapter EnrollPerson)
    //   id = XOR-fold(UUID)    → Electron direct sync's deterministic "código"
    // A registration-only lookup missed those and called it "already removed", so a
    // checked-out visitor kept their face on the reader.
    // Both derivations are only meaningful for a real person UUID — deriving an id
    // from an arbitrary registration string ("user:65cfa274…", an employee number)
    // yields a garbage slot number, so those stay a plain registration lookup.
    const isPersonUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);
    const asNumericId = /^\d+$/.test(identifier) ? Number(identifier) : NaN;
    const lookups: Array<{ filter: Record<string, any>; derived?: boolean }> =
      Number.isSafeInteger(asNumericId)
        ? [{ filter: { id: [asNumericId] } }, { filter: { registration: [identifier] } }]
        : isPersonUuid
          ? [
              { filter: { registration: [identifier] } },
              { filter: { id: [controlIDFnvUserId(identifier)] }, derived: true },
              { filter: { id: [uuidToDeterministicControlIDId(identifier)] }, derived: true },
            ]
          : [{ filter: { registration: [identifier] } }];

    try {
      // Step 1: Find the user on the device to get its actual numeric device ID
      let existingUser: any;
      const triedFilters = new Set<string>();
      for (const { filter, derived } of lookups) {
        const key = JSON.stringify(filter);
        if (triedFilters.has(key)) continue; // the two derivations can collide
        triedFilters.add(key);

        const loadResp = await this.controlIDRequest(device, 'load_objects.fcgi', {
          object: 'users',
          where: { users: filter },
        }, timeout);
        const candidate: any = loadResp?.users?.[0];
        if (!candidate) continue;

        // A derived id is a HASH — the slot can legitimately belong to somebody else.
        // Accept the match only when the device agrees it is this person (no
        // registration recorded, or exactly ours). Never delete a stranger.
        if (derived) {
          const candidateReg = candidate.registration;
          const belongsToSomeoneElse = typeof candidateReg === 'string'
            && candidateReg.trim() !== ''
            && candidateReg !== identifier;
          if (belongsToSomeoneElse) {
            console.warn(`[ControlID Remove] Derived id ${JSON.stringify(filter)} holds registration=${candidateReg}, not ${identifier} — refusing to delete a different person`);
            continue;
          }
        }

        existingUser = candidate;
        break;
      }

      if (!existingUser) {
        // Not found under ANY identifier the device could know it by — only now is
        // "already removed" an honest answer.
        console.log(`[ControlID Remove] User ${identifier} not found on device — treating as already removed`);
        attempts.push({ address: device.ip_address || device.local_ip_address || 'unknown', result: 'success', latencyMs: Date.now() - startTime });
        return {
          success: true,
          data: { message: 'User not found on device (already removed)' },
          mode: CommunicationMode.DIRECT,
          latencyMs: Date.now() - startTime,
          source,
          attempts,
          // The initial load_objects.fcgi lookup itself already IS the read-back —
          // absence was independently confirmed, not assumed.
          readbackVerified: true,
        };
      }

      const numericId: number = Number(existingUser.id);
      if (!Number.isSafeInteger(numericId)) {
        // Without a usable id the destroy payload would carry an empty filter, and
        // ControlID reads "no filter" as "every user" — never send that.
        console.error(`[ControlID Remove] Matched user for ${identifier} has no usable numeric id`, existingUser);
        attempts.push({ address: device.ip_address || device.local_ip_address || 'unknown', result: 'error', error: 'matched user has no numeric id', latencyMs: Date.now() - startTime });
        return {
          success: false,
          error: `removeControlIDPerson: user matched for ${identifier} has no usable numeric id — refusing to delete`,
          mode: CommunicationMode.DIRECT,
          latencyMs: Date.now() - startTime,
          source,
          attempts,
        };
      }
      console.log(`[ControlID Remove] Found user id=${numericId} for ${identifier}, deleting...`);

      // Step 2: Delete by numeric ID — most reliable format
      await this.controlIDRequest(device, 'destroy_objects.fcgi', {
        object: 'users',
        where: { users: { id: [numericId] } },
      }, timeout);

      console.log(` [ControlID Remove] Deleted user id=${numericId}`);

      // Step 3: independently confirm removal via a fresh read-back, instead of only
      // trusting that destroy_objects.fcgi didn't throw. Best-effort: if the
      // read-back itself fails, the delete is still reported as successful (the
      // primary command succeeded), but readbackVerified stays false so the caller
      // knows this is a weaker, unconfirmed signal.
      let readbackVerified = false;
      try {
        const verifyResp = await this.controlIDRequest(device, 'load_objects.fcgi', {
          object: 'users',
          where: { users: { id: [numericId] } },
        }, timeout);
        readbackVerified = !verifyResp?.users || verifyResp.users.length === 0;
        if (!readbackVerified) {
          console.warn(`[ControlID Remove] Read-back still shows user id=${numericId} present after delete`);
        }
      } catch (verifyErr) {
        console.warn('[ControlID Remove] Read-back verification failed (delete itself still reported success):', verifyErr);
      }

      attempts.push({ address: device.ip_address || device.local_ip_address || 'unknown', result: 'success', latencyMs: Date.now() - startTime });

      return {
        success: true,
        data: { deleted: numericId },
        mode: CommunicationMode.DIRECT,
        latencyMs: Date.now() - startTime,
        source,
        attempts,
        readbackVerified,
      };
    } catch (err: any) {
      console.error(` [ControlID Remove] Failed:`, err);
      attempts.push({ address: device.ip_address || device.local_ip_address || 'unknown', result: 'error', error: String(err), latencyMs: Date.now() - startTime });
      return {
        success: false,
        error: err?.message || 'ControlID remove failed',
        mode: CommunicationMode.DIRECT,
        latencyMs: Date.now() - startTime,
        source,
        attempts,
      };
    }
  }

  /**
   * ControlID-specific OPEN_DOOR: uses session auth + execute_actions.fcgi with sec_box action.
   * actionID=65793 = portaria(1) * 65536 + doorNo(257) — default door on iDFace/iDFlex devices.
   * RFID devices (controlid_rfid) use "door" action with "door=N" parameter instead.
   */
  private async openControlIDDoor(
    device: DeviceInfo,
    payload: any,
    startTime: number,
    attempts: DeviceOperationResult['attempts'],
    source: OperationSource,
    timeout: number,
  ): Promise<DeviceOperationResult> {
    // RFID/UHF terminals (iDUHF, iDBox) default to the "door" action instead of a sec_box
    // relay, since most don't have one wired up — but some installations DO wire an iDUHF
    // through a sec_box relay box, so use_sec_box must still be respected for RFID devices,
    // not hardcoded off. isRFID only picks the DEFAULT when use_sec_box hasn't been explicitly
    // configured, so existing devices' behavior doesn't change. Mirrors iot-connect's
    // ControlIDRFIDAdapter.OpenGate and ZonesDevices.tsx's ControlID — Acionamento section.
    const isRFID = device.device_type === 'controlid_rfid' || device.device_type === 'rfid_reader';
    const useSecBoxForRFID = device.config?.use_sec_box ?? !isRFID;
    const ip = device.ip_address || device.local_ip_address || '(no ip)';

    // Catraca (turnstile) devices: ControlID's own admin UI actuates these via the native
    // "catra" action (allow=clockwise/anticlockwise/both), NOT the generic "sec_box" relay
    // pulse — confirmed by inspecting a request captured straight from the device's built-in
    // web interface. sec_box is meant for simple electric locks; catra is the manufacturer's
    // real turnstile-authorization command. Direction is configurable via the "Sentido"
    // selector in ZonesDevices.tsx's "ControlID — Acionamento" panel (device.config.catraca_allow),
    // defaulting to "both" for devices that haven't set it.
    if (device.device_type === 'catraca') {
      const catracaAllow: 'both' | 'clockwise' | 'anticlockwise' = device.config?.catraca_allow ?? 'both';
      console.log(`[ControlID OpenDoor] ip=${ip} catraca (allow=${catracaAllow}) user=${device.username}`);
      try {
        const resp = await this.controlIDRequest(device, 'execute_actions.fcgi', {
          actions: [{ action: 'catra', parameters: `allow=${catracaAllow}, reason=3` }],
        }, timeout);
        console.log(`[ControlID OpenDoor] SUCCESS (catraca)`, resp);
        attempts.push({ address: ip, result: 'success', latencyMs: Date.now() - startTime });
        return {
          success: true,
          data: { opened: true },
          mode: CommunicationMode.DIRECT,
          latencyMs: Date.now() - startTime,
          source,
          attempts,
        };
      } catch (err: any) {
        console.error(`[ControlID OpenDoor] Failed (catraca):`, err);
        attempts.push({ address: ip, result: 'error', error: String(err), latencyMs: Date.now() - startTime });
        return {
          success: false,
          error: err?.message || 'ControlID catraca open failed',
          mode: CommunicationMode.DIRECT,
          latencyMs: Date.now() - startTime,
          source,
          attempts,
        };
      }
    }

    if (isRFID && !useSecBoxForRFID) {
      const doorNo: number = device.config?.controlid_door_number ?? 1;
      console.log(`[ControlID OpenDoor] ip=${ip} door=${doorNo} user=${device.username} (RFID/door action)`);
      try {
        const resp = await this.controlIDRequest(device, 'execute_actions.fcgi', {
          actions: [{ action: 'door', parameters: `door=${doorNo}` }],
        }, timeout);
        console.log(`[ControlID OpenDoor] SUCCESS`, resp);
        attempts.push({ address: ip, result: 'success', latencyMs: Date.now() - startTime });
        return {
          success: true,
          data: { opened: true },
          mode: CommunicationMode.DIRECT,
          latencyMs: Date.now() - startTime,
          source,
          attempts,
        };
      } catch (err: any) {
        console.error(`[ControlID OpenDoor] Failed:`, err);
        attempts.push({ address: ip, result: 'error', error: String(err), latencyMs: Date.now() - startTime });
        return {
          success: false,
          error: err?.message || 'ControlID door open failed',
          mode: CommunicationMode.DIRECT,
          latencyMs: Date.now() - startTime,
          source,
          attempts,
        };
      }
    }

    // ControlID door open via execute_actions.fcgi with sec_box action.
    // sec_box id = portaria * 65792 + saida  (portaria 1, saída 1 → 65793 confirmed working)
    //
    // Reaching this point means sec_box IS the actuation method: either the device is
    // RFID and useSecBoxForRFID was true (checked above), or it's a face device (iDFace/
    // iDFlex), which has no "door" action at all — sec_box is its ONLY way to open, so
    // config.use_sec_box must never block it here (mirrors the cloud backend's
    // TriggerControlIDRelay, which always uses sec_box for ControlID unconditionally).
    // A stray use_sec_box=false on a face device (e.g. from the shared RFID/face toggle
    // in ZonesDevices.tsx) must not disable actuation entirely.
    const saida: number = device.config?.controlid_saida ?? 1;
    const actionId: number = device.config?.controlid_sec_box_id ?? controlIDSecBoxId(saida);
    console.log(`[ControlID OpenDoor] ip=${ip} saida=${saida} sec_box_id=${actionId} user=${device.username}`);

    try {
      const resp = await this.controlIDRequest(device, 'execute_actions.fcgi', {
        actions: [{ action: 'sec_box', parameters: `id=${actionId}, reason=3` }],
      }, timeout);
      console.log(`[ControlID OpenDoor] SUCCESS`, resp);

      attempts.push({ address: ip, result: 'success', latencyMs: Date.now() - startTime });
      return {
        success: true,
        data: { opened: true },
        mode: CommunicationMode.DIRECT,
        latencyMs: Date.now() - startTime,
        source,
        attempts,
      };
    } catch (err: any) {
      console.error(`[ControlID OpenDoor] FAILED ip=${ip} saida=${saida} id=${actionId}:`, err?.message || err);
      attempts.push({ address: ip, result: 'error', error: String(err), latencyMs: Date.now() - startTime });
      throw err;
    }
  }

  /**
   * Build base URL for direct device requests (strips stored protocol prefix, handles embedded port).
   */
  private buildDeviceBaseUrl(device: DeviceInfo): string {
    let raw = (device.local_ip_address || device.ip_address || '').replace(/^https?:\/\//, '');
    let ip = raw;
    let port = device.port || '80';
    if (raw.includes(':')) { [ip, port] = raw.split(':'); }
    const protocol = port === '443' ? 'https' : 'http';
    return `${protocol}://${ip}:${port}`;
  }

  /**
   * Reboot a device. Hybrid: direct in Electron, cloud fallback.
   * - Hikvision: PUT /ISAPI/System/reboot (digest auth)
   * - ControlID: POST /execute_actions.fcgi { actions:[{action:"reboot"}] } (session auth)
   * - Cloud fallback: POST /api/v1/iot-connect/{prefix}/devices/{id}/reboot
   */
  async rebootDevice(device: DeviceInfo & { id: string }, isRFID = false): Promise<{ success: boolean; error?: string }> {
    const inElectron = isElectron() && !!(window as any).electronAPI?.deviceHttpRequest;
    const hasIp = device.local_ip_address || device.ip_address;

    if (inElectron && hasIp) {
      try {
        const base = this.buildDeviceBaseUrl(device);
        if (device.vendor === 'hikvision') {
          const result = await (window as any).electronAPI.deviceHttpRequest({
            url: `${base}/ISAPI/System/reboot`,
            method: 'PUT',
            headers: { 'Content-Type': 'application/xml' },
            body: '',
            timeout: 10000,
            digestAuth: { username: device.username, password: device.password },
          });
          if (result.success || result.statusCode === 200 || result.statusCode === 204) return { success: true };
        } else if (device.vendor === 'controlid') {
          const adapter = AdapterFactory.getAdapter('controlid');
          const sessionInfo = await sessionManager.getOrCreateSession(device, adapter);
          const headers: Record<string, string> = { 'Content-Type': 'application/json; charset=utf-8' };
          if (sessionInfo) Object.assign(headers, sessionInfo.headers);
          const result = await (window as any).electronAPI.deviceHttpRequest({
            url: `${base}/execute_actions.fcgi`,
            method: 'POST',
            headers,
            body: { actions: [{ action: 'reboot' }] },
            timeout: 10000,
          });
          if (result.success) return { success: true };
        }
      } catch (err: any) {
        console.warn('[DeviceComm] Direct reboot failed, trying cloud:', err);
      }
    }

    // Cloud fallback
    const prefix = isRFID ? 'rfid' : 'facial-recognition';
    try {
      await api.post(`iot-connect/${prefix}/devices/${device.id}/reboot`, {});
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Reboot failed' };
    }
  }

  /**
   * Set device clock to current BRT time (UTC-3). Hybrid: direct in Electron, cloud fallback.
   * - Hikvision: PUT /ISAPI/System/time with XML (digest auth)
   * - Cloud fallback: POST /api/v1/iot-connect/{prefix}/devices/{id}/set-time
   */
  async setDeviceTime(device: DeviceInfo & { id: string }, isRFID = false): Promise<{ success: boolean; error?: string }> {
    const inElectron = isElectron() && !!(window as any).electronAPI?.deviceHttpRequest;
    const hasIp = device.local_ip_address || device.ip_address;

    // Build BRT timestamp (UTC-3)
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const brt = new Date(now.getTime() - 3 * 60 * 60 * 1000);
    const localTime = `${brt.getUTCFullYear()}-${pad(brt.getUTCMonth() + 1)}-${pad(brt.getUTCDate())}T${pad(brt.getUTCHours())}:${pad(brt.getUTCMinutes())}:${pad(brt.getUTCSeconds())}-03:00`;

    if (inElectron && hasIp && device.vendor === 'hikvision') {
      try {
        const base = this.buildDeviceBaseUrl(device);
        const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<Time version="2.0" xmlns="http://www.hikvision.com/ver20/XMLSchema">\n  <timeMode>manual</timeMode>\n  <localTime>${localTime}</localTime>\n  <timeZone>CST-3:00:00</timeZone>\n</Time>`;
        const result = await (window as any).electronAPI.deviceHttpRequest({
          url: `${base}/ISAPI/System/time`,
          method: 'PUT',
          headers: { 'Content-Type': 'application/xml' },
          body: xml,
          timeout: 10000,
          digestAuth: { username: device.username, password: device.password },
        });
        if (result.success || result.statusCode === 200 || result.statusCode === 204) return { success: true };
      } catch (err: any) {
        console.warn('[DeviceComm] Direct set-time failed, trying cloud:', err);
      }
    }

    // Cloud fallback
    const prefix = isRFID ? 'rfid' : 'facial-recognition';
    try {
      await api.post(`iot-connect/${prefix}/devices/${device.id}/set-time`, {
        datetime: now.toISOString(),
        timezone: 'America/Sao_Paulo',
      });
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Set time failed' };
    }
  }

  /**
   * Open a device door directly (Hikvision local ISAPI) with cloud fallback.
   * Uses PUT /ISAPI/AccessControl/RemoteControl/door/1 with XML body.
   */
  async openDoor(
    deviceInfo: DeviceInfo,
    mode: CommunicationMode = CommunicationMode.HYBRID
  ): Promise<DeviceOperationResult> {
    return this.executeOperation(deviceInfo, DeviceOperations.OPEN_DOOR, { gate_id: 1 }, mode);
  }

  /**
   * Force re-sync RFID cards on a ControlID device — LOCAL SYNC ONLY (Electron direct connection).
   *
   * For each vehicle:
   *   - If user already exists on device: delete ALL their cards, then recreate with the
   *     correctly-parsed decimal card value (fixes old wrong Wiegand-split values).
   *   - If user not on device: run a full SYNC_PERSON (creates user + card with correct value).
   *
   * No cloud path — requires Electron (window.electronAPI) and a reachable device IP.
   */
  async forceReSyncControlIDRfidCards(
    device: DeviceInfo,
    vehicles: Array<{ vehicle_id: string; rfid_tag: string; plate?: string; person_name?: string }>,
    timeout = 15000,
    onProgress?: (done: number, total: number, success: number, failure: number) => void,
  ): Promise<{ success: number; failure: number; errors: string[] }> {
    let success = 0;
    let failure = 0;
    const errors: string[] = [];

    for (let i = 0; i < vehicles.length; i++) {
      const vehicle = vehicles[i];
      const label = vehicle.plate || vehicle.vehicle_id;
      try {
        const registration = vehicle.vehicle_id;

        // 1. Try to find user by registration UUID
        let existingUser: any = null;
        {
          const r = await this.controlIDRequest(device, 'load_objects.fcgi', {
            object: 'users',
            where: { users: { registration: [registration] } },
          }, timeout);
          existingUser = r?.users?.[0] ?? null;
        }

        // 2. If not found by registration, find via current card value (users created without registration).
        //    Try Wiegand-encoded value first (current format), then raw decimal as fallback for
        //    legacy cards that were registered before the Wiegand encoding fix (stored as plain decimal).
        if (!existingUser) {
          const cardValue    = this.parseRfidTagToCardValue(vehicle.rfid_tag, (vehicle as any).rfid_tag_format);   // Wiegand
          const rawDecValue  = this.parseRfidTagRawDecimal(vehicle.rfid_tag);    // legacy fallback
          let existingCard: any = null;

          if (cardValue !== null) {
            console.log(`[ForceRFIDSync] No registration match for ${label}, trying card lookup (wiegand=${cardValue})`);
            const cardResp = await this.controlIDRequest(device, 'load_objects.fcgi', {
              object: 'cards',
              where: { cards: { value: [cardValue] } },
            }, timeout);
            existingCard = cardResp?.cards?.[0] ?? null;
          }
          // Wiegand lookup missed → try raw decimal (legacy card on device from before Wiegand fix)
          if (!existingCard && rawDecValue !== null && rawDecValue !== cardValue) {
            console.log(`[ForceRFIDSync] Wiegand card not found for ${label}, trying raw decimal (value=${rawDecValue})`);
            const rawCardResp = await this.controlIDRequest(device, 'load_objects.fcgi', {
              object: 'cards',
              where: { cards: { value: [rawDecValue] } },
            }, timeout);
            existingCard = rawCardResp?.cards?.[0] ?? null;
          }

          if (existingCard?.user_id) {
              const userResp = await this.controlIDRequest(device, 'load_objects.fcgi', {
                object: 'users',
                where: { users: { id: [existingCard.user_id] } },
              }, timeout);
              const foundUser: any = userResp?.users?.[0] ?? null;
              if (foundUser) {
                if (foundUser.registration === registration) {
                  // Already a vehicle-mode user — proceed to the "user found" path to fix cards
                  console.log(`[ForceRFIDSync] Found vehicle-mode user id=${foundUser.id} via card lookup for ${label}`);
                  existingUser = foundUser;
                } else {
                  // Person-mode user owns the card. Migrate safely:
                  // 1. Create vehicle-mode user first (no card yet) — if this fails, card stays
                  //    on person-mode user and nothing regresses.
                  // 2. Only after vehicle-mode user exists: remove card from person-mode user.
                  // 3. Add card to vehicle-mode user.
                  console.log(`[ForceRFIDSync] Card owned by person-mode user id=${foundUser.id} for ${label} — migrating to vehicle-mode`);
                  const cardVal = this.parseRfidTagToCardValue(vehicle.rfid_tag, (vehicle as any).rfid_tag_format);
                  try {
                    // Step 1: create/ensure vehicle-mode user (rfid_tags:[] skips card addition)
                    const createResult = await this.executeOperation(device, DeviceOperations.SYNC_PERSON, {
                      person_id: registration,
                      first_name: vehicle.plate || 'Veículo',
                      last_name: vehicle.person_name || '',
                      identity_photo_url: undefined,
                      rfid_tags: [],
                      vehicle_plate: vehicle.plate,
                      isAllowed: true,
                    }, CommunicationMode.DIRECT);
                    if (!createResult.success) {
                      throw new Error(createResult.error || 'SYNC_PERSON failed');
                    }
                    // Step 2: remove card from person-mode user
                    await this.controlIDRequest(device, 'destroy_objects.fcgi', {
                      object: 'cards',
                      where: { cards: { id: [existingCard.id] } },
                    }, timeout);
                    // Step 3: fetch vehicle-mode user id, add card
                    if (cardVal !== null) {
                      const vmResp = await this.controlIDRequest(device, 'load_objects.fcgi', {
                        object: 'users',
                        where: { users: { registration: [registration] } },
                      }, timeout);
                      const vmUser = vmResp?.users?.[0];
                      if (vmUser) {
                        await this.controlIDRequest(device, 'create_objects.fcgi', {
                          object: 'cards',
                          values: [{ value: cardVal, user_id: vmUser.id }],
                        }, timeout);
                        console.log(`[ForceRFIDSync] Migrated card to vehicle-mode user id=${vmUser.id} for ${label}`);
                      }
                    }
                    success++;
                    onProgress?.(i + 1, vehicles.length, success, failure);
                    continue;
                  } catch (migErr: any) {
                    console.warn(`[ForceRFIDSync] Migration failed for ${label}: ${migErr?.message} — card left on person-mode user`);
                    failure++;
                    errors.push(`${label}: ${migErr?.message}`);
                    onProgress?.(i + 1, vehicles.length, success, failure);
                    continue;
                  }
                }
              }
            }
          }
        // ── User truly not on device → full SYNC_PERSON to create user + card ──
        if (!existingUser) {
          console.log(`[ForceRFIDSync] User not found for ${label}, running SYNC_PERSON`);
          const syncResult = await this.executeOperation(
            device,
            DeviceOperations.SYNC_PERSON,
            {
              person_id: registration,
              first_name: vehicle.plate || 'Veículo',
              last_name: vehicle.person_name || '',
              identity_photo_url: undefined,
              rfid_tags: [vehicle.rfid_tag],
              vehicle_plate: vehicle.plate,
              isAllowed: true,
            },
            CommunicationMode.DIRECT,
          );
          if (syncResult.success) {
            success++;
          } else {
            failure++;
            errors.push(`${label}: ${syncResult.error}`);
          }
        } else {
          // User found — delete ALL existing cards, then recreate with correct decimal value
          const deviceUserId: number = existingUser.id;
          console.log(`[ForceRFIDSync] Found user id=${deviceUserId} for ${label}, clearing old cards`);

          // Delete all cards for this user (best-effort — non-fatal if none exist)
          try {
            await this.controlIDRequest(device, 'destroy_objects.fcgi', {
              object: 'cards',
              where: { cards: { user_id: [deviceUserId] } },
            }, timeout);
            console.log(`[ForceRFIDSync] Cleared cards for user id=${deviceUserId}`);
          } catch (delErr: any) {
            console.warn(`[ForceRFIDSync] Card delete best-effort failed (may have no cards): ${delErr?.message}`);
          }

          // Parse correct decimal card value
          const cardValue = this.parseRfidTagToCardValue(vehicle.rfid_tag, (vehicle as any).rfid_tag_format);
          if (cardValue === null) {
            // No rfid_tag in DB — vehicle-mode user exists with vehicleUUID, no card needed
            console.log(`[ForceRFIDSync] No RFID tag for ${label} — user registered in vehicle-mode (no card)`);
            success++;
            onProgress?.(i + 1, vehicles.length, success, failure);
            continue;
          }

          // Remove any card with the Wiegand value OR legacy raw-decimal value owned by another user.
          // This ensures old person-mode cards (stored as raw decimal before the Wiegand fix)
          // are cleaned up so the device exclusively routes the tag to the vehicle-mode user.
          const rawDecValue2 = this.parseRfidTagRawDecimal(vehicle.rfid_tag);
          const valuesToClean = [cardValue, ...(rawDecValue2 !== null && rawDecValue2 !== cardValue ? [rawDecValue2] : [])];
          for (const val of valuesToClean) {
            try {
              const conflictResp = await this.controlIDRequest(device, 'load_objects.fcgi', {
                object: 'cards',
                where: { cards: { value: [val] } },
              }, timeout);
              const conflictCards: any[] = conflictResp?.cards || [];
              for (const cc of conflictCards) {
                if (cc.user_id !== deviceUserId) {
                  console.warn(`[ForceRFIDSync] Card value ${val} owned by other user id=${cc.user_id} — removing legacy card`);
                  await this.controlIDRequest(device, 'destroy_objects.fcgi', {
                    object: 'cards',
                    where: { cards: { id: [cc.id] } },
                  }, timeout);
                }
              }
            } catch (conflictErr: any) {
              console.warn(`[ForceRFIDSync] Conflict check for value ${val} failed (non-fatal): ${conflictErr?.message}`);
            }
          }

          // Recreate card with correct value
          await this.controlIDRequest(device, 'create_objects.fcgi', {
            object: 'cards',
            values: [{ value: cardValue, user_id: deviceUserId }],
          }, timeout);
          console.log(`[ForceRFIDSync] Card recreated for user id=${deviceUserId}: "${vehicle.rfid_tag}" → ${cardValue}`);
          success++;
        }
      } catch (err: any) {
        failure++;
        errors.push(`${label}: ${err?.message || String(err)}`);
        console.error(`[ForceRFIDSync] Error for ${label}:`, err);
      }
      onProgress?.(i + 1, vehicles.length, success, failure);
    }

    return { success, failure, errors };
  }

  /**
   * Ensure the ControlID device has a default group, time zone, access rule, and portal links.
   * Idempotent — safe to call multiple times (results are cached per device).
   * Returns the group ID to use when assigning users, or null on failure.
   *
   * ControlID access model:
   *   user → user_groups → group → group_access_rules → access_rule → time_zone_itens (horário)
   *                                                              ↓
   *                                              portal_access_rules → portal (door)
   */
  async ensureControlIDDefaultAccess(device: DeviceInfo, timeout = 15000): Promise<number | null> {
    if (device.vendor !== 'controlid') return null;

    // Return cached result (avoids re-querying every person in the loop)
    if (this.controlIDGroupCache.has(device.id)) {
      return this.controlIDGroupCache.get(device.id)!;
    }

    const PADRAO_GROUP_NAME  = 'Padrão';
    const FALLBACK_GROUP_NAME = 'Acesso Geral';
    const DEFAULT_TZ_NAME    = 'Sempre';
    const DEFAULT_RULE_NAME  = 'Acesso Total';

    // Helper: load objects of a given type, returns the typed array
    const load = async (object: string, where?: any): Promise<any[]> => {
      const payload: any = { object };
      if (where) payload.where = { [object]: where };
      const resp = await this.controlIDRequest(device, 'load_objects.fcgi', payload, timeout);
      return resp?.[object] || [];
    };

    // Helper: create objects, returns ids array
    const create = async (object: string, values: any[]): Promise<number[]> => {
      const resp = await this.controlIDRequest(device, 'create_objects.fcgi', { object, values }, timeout);
      const ids: number[] = resp?.ids || [];
      console.log(` [ControlID setup] Created ${object}:`, ids);
      return ids;
    };

    try {
      console.log(` [ControlID] Ensuring default access setup for device ${device.id}...`);

      // ── Step 1: Find the best group to use ───────────────────────────────
      // Priority: "Padrão" (factory default, ID=1) > any group with existing access chain > create new
      const groups = await load('groups');
      console.log(` [ControlID] Existing groups:`, groups.map((g: any) => `${g.name}(${g.id})`));

      // Try "Padrão" first — the factory default group that already exists on every device
      let group = groups.find((g: any) => g.name === PADRAO_GROUP_NAME);

      // If "Padrão" found, check if it already has a working access chain (portal access rules)
      if (group) {
        const existingGroupRules = await load('group_access_rules', { group_id: [group.id] });
        if (existingGroupRules.length > 0) {
          const existingRuleId = existingGroupRules[0].access_rule_id;
          const existingPortalRules = await load('portal_access_rules', { access_rule_id: [existingRuleId] });
          if (existingPortalRules.length > 0) {
            // "Padrão" already has a fully configured access chain — use it directly
            this.controlIDGroupCache.set(device.id, group.id);
            console.log(` [ControlID] Using existing "${PADRAO_GROUP_NAME}" group (id=${group.id}) with pre-configured access chain`);
            return group.id;
          }
        }
        console.log(` [ControlID] "${PADRAO_GROUP_NAME}" group found but access chain incomplete — will configure it`);
      }

      // Fall back to our custom group if "Padrão" not found
      if (!group) {
        group = groups.find((g: any) => g.name === FALLBACK_GROUP_NAME);
        if (!group) {
          const ids = await create('groups', [{ name: FALLBACK_GROUP_NAME }]);
          group = { id: ids[0], name: FALLBACK_GROUP_NAME };
        } else {
          console.log(` [ControlID] Group "${FALLBACK_GROUP_NAME}" already exists (id=${group.id})`);
        }
      }
      const groupId: number = group.id;

      // ── Step 2: Get or create "Sempre" time zone ─────────────────────────
      const timeZones = await load('time_zones');
      console.log(` [ControlID] Existing time zones:`, timeZones.map((t: any) => `${t.name}(${t.id})`));
      let tz = timeZones.find((t: any) => t.name === DEFAULT_TZ_NAME);
      if (!tz) {
        const ids = await create('time_zones', [{ name: DEFAULT_TZ_NAME }]);
        tz = { id: ids[0] };
      } else {
        console.log(` [ControlID] Time zone "${DEFAULT_TZ_NAME}" already exists (id=${tz.id})`);
      }
      const timeZoneId: number = tz.id;

      // ── Step 3: Ensure time span exists (24/7) ───────────────────────────
      const tzItems = await load('time_spans', { time_zone_id: [timeZoneId] });
      console.log(` [ControlID] time_spans for time_zone_id=${timeZoneId}:`, tzItems);
      if (tzItems.length === 0) {
        await create('time_spans', [{
          time_zone_id: timeZoneId,
          start: 0,
          end: 86399,
          sun: 1, mon: 1, tue: 1, wed: 1, thu: 1, fri: 1, sat: 1,
          hol1: 1, hol2: 1, hol3: 1,
        }]);
      } else {
        console.log(` [ControlID] time_spans already exist, skipping`);
      }

      // ── Step 4: Get or create access rule ────────────────────────────────
      // Try factory default "Sempre Liberado" (ID=1) first, then our custom rule
      const accessRules = await load('access_rules');
      console.log(` [ControlID] Existing access rules:`, accessRules.map((r: any) => `${r.name}(${r.id})`));
      let rule = accessRules.find((r: any) => r.name === 'Sempre Liberado') ||
                 accessRules.find((r: any) => r.name === DEFAULT_RULE_NAME);
      if (!rule) {
        const ids = await create('access_rules', [{ name: DEFAULT_RULE_NAME }]);
        rule = { id: ids[0] };
      } else {
        console.log(` [ControlID] Access rule "${rule.name}" already exists (id=${rule.id})`);
      }
      const accessRuleId: number = rule.id;

      // ── Step 4b: Link access rule → time zone ────────────────────────────
      const ruleTimeZones = await load('access_rule_time_zones', { access_rule_id: [accessRuleId] });
      console.log(` [ControlID] access_rule_time_zones for rule ${accessRuleId}:`, ruleTimeZones);
      const hasTzLink = ruleTimeZones.some((r: any) => r.time_zone_id === timeZoneId);
      if (!hasTzLink) {
        await create('access_rule_time_zones', [{ access_rule_id: accessRuleId, time_zone_id: timeZoneId }]);
      } else {
        console.log(` [ControlID] access_rule_time_zones already linked, skipping`);
      }

      // ── Step 5: Ensure group has this access rule ─────────────────────────
      const groupRules = await load('group_access_rules', { group_id: [groupId] });
      console.log(` [ControlID] group_access_rules for group ${groupId}:`, groupRules);
      const hasGroupRule = groupRules.some((r: any) => r.access_rule_id === accessRuleId);
      if (!hasGroupRule) {
        await create('group_access_rules', [{ group_id: groupId, access_rule_id: accessRuleId }]);
      } else {
        console.log(` [ControlID] group_access_rule already linked, skipping`);
      }

      // ── Step 6: Ensure each portal has this access rule ──────────────────
      const portals = await load('portals');
      const portalRules = await load('portal_access_rules', { access_rule_id: [accessRuleId] });
      const linkedPortalIds = new Set(portalRules.map((r: any) => r.portal_id));
      console.log(` [ControlID] Portals: ${portals.length}, already linked: ${linkedPortalIds.size}`);
      for (const portal of portals) {
        if (!linkedPortalIds.has(portal.id)) {
          await create('portal_access_rules', [{ portal_id: portal.id, access_rule_id: accessRuleId }]);
        }
      }

      this.controlIDGroupCache.set(device.id, groupId);
      console.log(` [ControlID] Access setup complete — group="${group.name}" id=${groupId}`);
      return groupId;
    } catch (err: any) {
      console.error(` [ControlID] Failed to ensure default access setup:`, err?.message || err);
      return null;
    }
  }

  /**
   * Re-upload face photo for a user that already exists on the device.
   * Wraps the private uploadFacePhoto so callers can trigger it with a known device user ID.
   */
  async reuploadFacePhoto(
    device: DeviceInfo,
    deviceUserId: number | string,
    personData: PersonData,
    timeout = 15000
  ): Promise<void> {
    await this.uploadFacePhoto(device, deviceUserId, personData, timeout);
  }

  /**
   * Assign a user to a group on ControlID device.
   * Call this after each successful SYNC_PERSON to grant the user access.
   */
  async assignControlIDUserToGroup(
    device: DeviceInfo,
    deviceUserId: number,
    groupId: number,
    timeout = 15000
  ): Promise<void> {
    await this.controlIDRequest(device, 'create_objects.fcgi', {
      object: 'user_groups',
      values: [{ user_id: deviceUserId, group_id: groupId }],
    }, timeout);
    console.log(` [ControlID] User ${deviceUserId} assigned to group ${groupId}`);
  }

  /**
   * Assign ALL users on the device to a group (bulk idempotent).
   * Fetches existing group membership first so already-assigned users are skipped.
   * Call this after sync loop to ensure every user (including pre-existing ones) has access.
   */
  async assignAllDeviceUsersToGroup(
    device: DeviceInfo,
    groupId: number,
    timeout = 15000
  ): Promise<{ assigned: number; skipped: number }> {
    // Load all users on device
    const allUsersResp = await this.controlIDRequest(
      device, 'load_objects.fcgi', { object: 'users' }, timeout
    );
    const parse = (d: any) => {
      if (typeof d === 'string') { try { return JSON.parse(d); } catch { return d; } }
      return d;
    };
    const allUsers: any[] = parse(allUsersResp)?.users || [];

    // Load existing user_groups entries for this group
    const assignedResp = await this.controlIDRequest(
      device, 'load_objects.fcgi',
      { object: 'user_groups', where: { user_groups: { group_id: [groupId] } } },
      timeout
    );
    const assignedUserIds = new Set<number>(
      (parse(assignedResp)?.user_groups || []).map((r: any) => Number(r.user_id))
    );

    let assigned = 0;
    let skipped = 0;
    for (const user of allUsers) {
      const uid = Number(user.id);
      if (!assignedUserIds.has(uid)) {
        await this.controlIDRequest(device, 'create_objects.fcgi', {
          object: 'user_groups',
          values: [{ user_id: uid, group_id: groupId }],
        }, timeout);
        assigned++;
        console.log(` [ControlID] Bulk assign: user ${uid} → group ${groupId}`);
      } else {
        skipped++;
      }
    }

    console.log(` [ControlID] Bulk group assignment done — assigned: ${assigned}, skipped: ${skipped}`);
    return { assigned, skipped };
  }

  /**
   * Directly configure a ControlID device to push events to the cloud webhook URL.
   * Two-step: POST /login.fcgi → POST /set_configuration.fcgi?session={token}.
   * Only used in Electron mode (Electron main process can reach local-network devices).
   */
  private async directControlIDConfigureMonitor(
    device: DeviceInfo,
    payload: { hostname: string; port: string; path: string; request_timeout: string },
    startTime: number,
    attempts: DeviceOperationResult['attempts'],
    source: OperationSource,
    timeout: number,
  ): Promise<DeviceOperationResult> {
    if (!window.electronAPI?.deviceHttpRequest) {
      return {
        success: false,
        attempts,
        error: 'Electron API not available',
        mode: CommunicationMode.DIRECT,
        latencyMs: Date.now() - startTime,
        source,
      };
    }

    let rawIp = source === 'local'
      ? (device.local_ip_address || device.ip_address)
      : device.ip_address;
    // Strip protocol prefix stored in DB (e.g., "http://203.0.113.26:1029")
    rawIp = (rawIp || '').replace(/^https?:\/\//, '');
    // Parse port embedded in IP string (e.g., "203.0.113.26:1029")
    let deviceIp = rawIp;
    let devicePort = device.port || '80';
    if (rawIp.includes(':')) {
      const [h, p] = rawIp.split(':');
      deviceIp = h;
      devicePort = p;
    }
    const protocol = devicePort === '443' ? 'https' : 'http';

    // Step 1: login.fcgi → get session token
    const loginUrl = `${protocol}://${deviceIp}:${devicePort}/login.fcgi`;
    console.log(' [ConfigureMonitor] ControlID login:', loginUrl);

    const loginResult = await window.electronAPI.deviceHttpRequest({
      url: loginUrl,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login: device.username, password: device.password }),
      timeout,
    });

    if (!loginResult.success || !loginResult.data?.session) {
      attempts.push({ address: source === 'local' ? 'local' : 'public', result: 'error', latencyMs: loginResult.timeMs });
      return {
        success: false,
        attempts,
        error: loginResult.error || 'ControlID login failed — no session token',
        mode: CommunicationMode.DIRECT,
        latencyMs: Date.now() - startTime,
        source,
      };
    }

    const session = loginResult.data.session as string;

    // Step 2: set_configuration.fcgi?session={token}
    const configUrl = `${protocol}://${deviceIp}:${devicePort}/set_configuration.fcgi?session=${encodeURIComponent(session)}`;
    console.log(' [ConfigureMonitor] ControlID set_configuration:', configUrl, payload);

    const configResult = await window.electronAPI.deviceHttpRequest({
      url: configUrl,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ monitor: payload }),
      timeout,
    });

    attempts.push({
      address: source === 'local' ? 'local' : 'public',
      result: configResult.success ? 'success' : 'error',
      latencyMs: configResult.timeMs,
    });

    if (!configResult.success) {
      return {
        success: false,
        attempts,
        error: configResult.error,
        mode: CommunicationMode.DIRECT,
        latencyMs: Date.now() - startTime,
        source,
      };
    }

    return {
      success: true,
      data: configResult.data,
      attempts,
      mode: CommunicationMode.DIRECT,
      latencyMs: Date.now() - startTime,
      source,
    };
  }

  /**
   * Directly configure a Hikvision device to push events to the cloud webhook URL.
   * Makes a PUT request to /ISAPI/Event/notification/httpHosts with an XML body.
   * Only used in Electron mode (Electron main process can reach local-network devices).
   */
  private async directHikvisionConfigureWebhook(
    device: DeviceInfo,
    payload: { webhookUrl: string; hostname: string; port: number; useHttps: boolean },
    startTime: number,
    attempts: DeviceOperationResult['attempts'],
    source: OperationSource,
    timeout: number,
  ): Promise<DeviceOperationResult> {
    if (!window.electronAPI?.deviceHttpRequest) {
      return {
        success: false,
        attempts,
        error: 'Electron API not available',
        mode: CommunicationMode.DIRECT,
        latencyMs: Date.now() - startTime,
        source,
      };
    }

    let rawIpHik = source === 'local'
      ? (device.local_ip_address || device.ip_address)
      : device.ip_address;
    // Strip protocol prefix stored in DB (e.g., "http://192.168.1.100:8008")
    rawIpHik = (rawIpHik || '').replace(/^https?:\/\//, '');
    // Parse port embedded in IP string
    let deviceIp = rawIpHik;
    let devicePort = device.port || '80';
    if (rawIpHik.includes(':')) {
      const [h, p] = rawIpHik.split(':');
      deviceIp = h;
      devicePort = p;
    }
    const protocol = devicePort === '443' ? 'https' : 'http';
    const endpoint = `${protocol}://${deviceIp}:${devicePort}/ISAPI/Event/notification/httpHosts`;

    const protocolType = payload.useHttps ? 'HTTPS' : 'HTTP';
    const xmlBody = `<?xml version="1.0" encoding="UTF-8"?>
<HttpHostNotificationList version="2.0" xmlns="http://www.hikvision.com/ver20/XMLSchema">
    <HttpHostNotification version="2.0" xmlns="http://www.hikvision.com/ver20/XMLSchema">
        <id>1</id>
        <url>${payload.webhookUrl}</url>
        <protocolType>${protocolType}</protocolType>
        <parameterFormatType>XML</parameterFormatType>
        <addressingFormatType>hostname</addressingFormatType>
        <hostName>${payload.hostname}</hostName>
        <portNo>${payload.port}</portNo>
        <httpAuthenticationMethod>none</httpAuthenticationMethod>
    </HttpHostNotification>
</HttpHostNotificationList>`;

    console.log(' [ConfigureWebhook] Hikvision direct call:', { endpoint, webhookUrl: payload.webhookUrl });

    const result = await window.electronAPI.deviceHttpRequest({
      url: endpoint,
      method: 'PUT',
      headers: { 'Content-Type': 'application/xml' },
      body: xmlBody,
      timeout,
      digestAuth: { username: device.username, password: device.password },
    });

    attempts.push({
      address: source === 'local' ? 'local' : 'public',
      result: result.success ? 'success' : 'error',
      latencyMs: result.timeMs,
    });

    if (!result.success) {
      return {
        success: false,
        attempts,
        error: result.error,
        mode: CommunicationMode.DIRECT,
        latencyMs: Date.now() - startTime,
        source,
      };
    }

    return {
      success: true,
      data: result.data,
      attempts,
      mode: CommunicationMode.DIRECT,
      latencyMs: Date.now() - startTime,
      source,
    };
  }

  /**
   * Enroll a fingerprint on a ControlID device.
   *
   * Direct mode (Electron): calls enroll_finger.fcgi (blocks ~60s while user places finger 3×),
   * then load_objects.fcgi to fetch the captured template. Returns { templateB64 }.
   *
   * Cloud mode (browser / fallback): delegates to condo-manager /fingerprints/enroll
   * which calls iot-connect, which calls the device. Returns nothing meaningful (server saves it).
   *
   * @param device     - ControlID device info
   * @param personCode - Person UUID (used as registration / person_code on device)
   * @param fingerType - 0=Indicador Direito, 1=Indicador Esquerdo, 2=Polegar D, 3=Polegar E, etc.
   * @param mode       - HYBRID (default): tries direct Electron first, falls back to cloud
   * @returns { templateB64?: string } on direct success; empty on cloud success
   */
  async enrollFingerprint(
    device: DeviceInfo,
    personCode: string,
    fingerType: number,
    mode: CommunicationMode = CommunicationMode.HYBRID,
    personName?: string,
  ): Promise<{ templateB64?: string; mode: CommunicationMode; error?: string }> {
    // Cloud-only: browsers cannot reach device directly
    if (mode === CommunicationMode.CLOUD || !isElectron()) {
      return { mode: CommunicationMode.CLOUD };
    }

    const adapter = AdapterFactory.getAdapter('controlid') as any;

    // Resolve IP + port
    const rawIp = (device.local_ip_address || device.ip_address || '').replace(/^https?:\/\//, '');
    let deviceIp = rawIp;
    let port = device.port || '80';
    if (rawIp.includes(':')) {
      const [h, p] = rawIp.split(':');
      deviceIp = h;
      port = p;
    }
    const protocol = port === '443' ? 'https' : 'http';
    const base = `${protocol}://${deviceIp}:${port}`;

    // Build auth headers — same pattern as controlIDRequest (Cookie only, no sessionAuth/digestAuth)
    const sessionInfo = await sessionManager.getOrCreateSession(device, adapter);
    const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
    if (sessionInfo) Object.assign(headers, sessionInfo.headers);

    const parseBody = (raw: any) =>
      typeof raw === 'string'
        ? (() => { try { return JSON.parse(raw); } catch { return raw; } })()
        : raw;

    // ── Step 0: resolve the person's device user_id ──────────────────────────────
    // Try UUID registration first (new sync path), then FNV numeric id (Go backend path).
    // If person isn't on device at all, auto-create them so we can enroll the fingerprint.
    // The template is stored in the backend and pushed to all actual access devices afterward.
    const deterministicId = uuidToDeterministicControlIDId(personCode);

    // FNV-1a hash (Go backend uses this range 1–99,999,998) — different from XOR-fold above
    const fnv1aId = controlIDFnvUserId(personCode);

    console.log(`[FP Enroll] Looking up person ${personCode} on device ${base}`);
    console.log(`[FP Enroll] Session present: ${!!sessionInfo}, deterministicId=${deterministicId}, fnv1aId=${fnv1aId}`);

    const doLookup = async (where: Record<string, any>): Promise<{ ok: boolean; id: number | null }> => {
      const res = await (window as any).electronAPI!.deviceHttpRequest({
        url: `${base}/load_objects.fcgi`,
        method: 'POST',
        headers,
        body: { object: 'users', where: { users: where } },
        timeout: 10000,
      });
      if (!res.success) return { ok: false, id: null };
      return { ok: true, id: parseBody(res.data)?.users?.[0]?.id ?? null };
    };

    let lookupCommError = false;
    let deviceUserID: number | null = null;

    // Strategy 1: registration = UUID
    const r1 = await doLookup({ registration: [personCode] });
    if (!r1.ok) { lookupCommError = true; }
    else { deviceUserID = r1.id; }
    console.log(`[FP Enroll] By UUID: ok=${r1.ok} id=${r1.id}`);

    // Strategy 2: id = FNV-1a (Go backend sync path)
    if (!lookupCommError && !deviceUserID) {
      const r2 = await doLookup({ id: [fnv1aId] });
      deviceUserID = r2.id;
      console.log(`[FP Enroll] By fnv1aId=${fnv1aId}: ok=${r2.ok} id=${r2.id}`);
    }

    // Strategy 3: id = XOR-fold (Electron direct sync path)
    if (!lookupCommError && !deviceUserID) {
      const r3 = await doLookup({ id: [deterministicId] });
      deviceUserID = r3.id;
      console.log(`[FP Enroll] By deterministicId=${deterministicId}: ok=${r3.ok} id=${r3.id}`);
    }

    if (lookupCommError) {
      return { mode: CommunicationMode.DIRECT, error: 'Erro ao comunicar com dispositivo (falha de conexão)' };
    }

    // Person not on device — auto-create so we can enroll the fingerprint.
    // The template will be saved to the backend and synced to actual access devices.
    if (!deviceUserID) {
      console.log(`[FP Enroll] Person not on device, auto-creating for enrollment (id=${deterministicId})`);
      const createRes = await (window as any).electronAPI!.deviceHttpRequest({
        url: `${base}/create_objects.fcgi`,
        method: 'POST',
        headers,
        body: {
          object: 'users',
          values: [{
            id: deterministicId,
            registration: personCode,
            name: personName || personCode.slice(0, 8),
          }],
        },
        timeout: 10000,
      });
      console.log(`[FP Enroll] Auto-create result: success=${createRes.success}`, parseBody(createRes.data));

      if (!createRes.success) {
        return { mode: CommunicationMode.DIRECT, error: `Não foi possível criar usuário no dispositivo: ${createRes.error}` };
      }

      // Re-lookup to get the device-assigned user_id (device may reassign)
      const r4 = await doLookup({ registration: [personCode] });
      deviceUserID = r4.id ?? deterministicId;
      console.log(`[FP Enroll] After auto-create, deviceUserID=${deviceUserID}`);
    }

    console.log(`[FP Enroll] Resolved device user_id=${deviceUserID} for person ${personCode}`);

    // ── Step 1: enroll fingerprint via remote_enroll.fcgi ────────────────────
    // ControlID API: POST /remote_enroll.fcgi?session=TOKEN
    // Body: { type: "biometry", user_id, save: true, sync: true }
    // sync=true → blocks until 3 captures complete.
    const sessionToken = (sessionInfo?.headers?.['Cookie'] || sessionInfo?.headers?.['cookie'] || '').replace('session=', '').trim();
    const enrollPayload = { type: 'biometry', user_id: deviceUserID, save: true, sync: true };
    console.log(`[FP Enroll] Calling remote_enroll.fcgi user_id=${deviceUserID}`, enrollPayload);

    const enrollResult = await (window as any).electronAPI!.deviceHttpRequest({
      url: `${base}/remote_enroll.fcgi?session=${sessionToken}`,
      method: 'POST',
      headers,
      body: enrollPayload,
      timeout: 75000,
    });

    console.log(`[FP Enroll] remote_enroll result: success=${enrollResult.success}`, enrollResult.data ?? enrollResult.error);

    if (!enrollResult.success) {
      sessionManager.invalidateSession(device);
      return { mode: CommunicationMode.DIRECT, error: `Dispositivo recusou matrícula: ${enrollResult.error || 'erro desconhecido'}` };
    }

    const enrollData = parseBody(enrollResult.data);
    if (enrollData?.success === false || enrollData?.error) {
      return {
        mode: CommunicationMode.DIRECT,
        error: `Erro no dispositivo: ${enrollData.error || 'falha desconhecida'}`,
      };
    }

    // ── Step 2: fetch biometric template from the `templates` table ──────────
    // After enrollment, ControlID stores the template in the `templates` object
    // (NOT `fingers` — that's a different object). The field is `template` (base64).
    // We refresh the session first since the 75 s enrollment may have expired it.
    sessionManager.invalidateSession(device);
    const freshSession = await sessionManager.getOrCreateSession(device, adapter);
    const freshHeaders: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
    if (freshSession) Object.assign(freshHeaders, freshSession.headers);

    const fetchTemplate = async (): Promise<string | null> => {
      const res = await (window as any).electronAPI!.deviceHttpRequest({
        url: `${base}/load_objects.fcgi`,
        method: 'POST',
        headers: freshHeaders,
        body: {
          object: 'templates',
          where: { templates: { user_id: [deviceUserID] } },
        },
        timeout: 10000,
      });
      console.log(`[FP Enroll] templates fetch:`, parseBody(res.data));
      if (!res.success) return null;
      const templates: any[] = parseBody(res.data)?.templates || [];
      // finger_type=0 means common/normal; finger_type=1 means panic — prefer common
      const tpl = templates.find((t: any) => t.finger_type === 0) || templates[0];
      return tpl?.template || null;
    };

    // Retry: 2 s → 4 s → 6 s (device may need a moment to persist after enrollment)
    const retryDelays = [2000, 4000, 6000];
    for (const delay of retryDelays) {
      await new Promise(r => setTimeout(r, delay));
      console.log(`[FP Enroll] Fetching template for user_id=${deviceUserID} (after ${delay}ms delay)`);
      const templateB64 = await fetchTemplate();
      if (templateB64) {
        console.log(`[FP Enroll] Got template from templates table, length=${templateB64.length}`);
        return { templateB64, mode: CommunicationMode.DIRECT };
      }
    }

    console.log(`[FP Enroll] Template not found in templates table — fingerprint saved on device only`);
    return { enrolledOnly: true, mode: CommunicationMode.DIRECT } as any;
  }
}

// Export singleton instance
export const deviceCommunicationService = new DeviceCommunicationService();

/**
 * Generate a deterministic positive int32 from a UUID string.
 * Used to ensure the same person gets the same numeric "código" (id) on every ControlID device.
 *
 * Strategy: XOR-fold all 128 UUID bits into 32 bits, then mask to positive int31.
 * Range: 1 – 2,147,483,647  (~2.1 billion).
 *
 * Collision probability for N users ≈ N² / (2 × 2.1B).
 * For 10 000 users per device: ~0.002 % — practically impossible in any real deployment.
 */
export function uuidToDeterministicControlIDId(uuid: string): number {
  const hex = uuid.replace(/-/g, ''); // 32 hex chars = 128 bits
  const h1 = parseInt(hex.substring(0,  8), 16); // bits 0–31
  const h2 = parseInt(hex.substring(8,  16), 16); // bits 32–63
  const h3 = parseInt(hex.substring(16, 24), 16); // bits 64–95
  const h4 = parseInt(hex.substring(24, 32), 16); // bits 96–127
  // XOR all chunks, force unsigned 32-bit, then strip sign bit → positive int31
  const folded = ((h1 ^ h2 ^ h3 ^ h4) >>> 0) & 0x7FFFFFFF;
  return folded + 1; // +1 so id is never 0
}

/**
 * Generate the ControlID numeric user id the way the GO BACKEND does — FNV-1a over
 * the person code, range 1–99,999,998.
 *
 * Must stay byte-for-byte equivalent to GenerateUserIDFromCode in
 * iot-connect/adapters/controlid/controlid_rfid_adapter.go (basis 2166136261,
 * prime 16777619, % 99999998 + 1), because a person enrolled by the cloud sync
 * path sits on the device under THIS id. Resolving them any other way misses.
 */
export function controlIDFnvUserId(personCode: string): number {
  let hash = 2166136261; // FNV-1a offset basis
  for (let i = 0; i < personCode.length; i++) {
    hash ^= personCode.charCodeAt(i);
    hash = (hash * 16777619) >>> 0; // uint32 multiply
  }
  return (hash % 99999998) + 1;
}
